// src/utils/enhancedPdfGenerator.js
import jsPDF from "jspdf";
import "jspdf-autotable";

// Enhanced professional color palette (RGB values)
const COLORS = {
  primary: [28, 69, 135], // Corporate Navy
  secondary: [68, 85, 105], // Slate Gray
  accent: [65, 105, 175], // Soft Blue
  success: [46, 125, 50], // Muted Green
  warning: [230, 149, 0], // Muted Orange
  danger: [211, 47, 47], // Muted Red
  info: [28, 69, 135], // Mapped to Primary
  indigo: [40, 53, 147], // Corporate Indigo
  teal: [0, 105, 92], // Corporate Teal
  orange: [216, 67, 21], // Corporate Orange
  light: [248, 250, 252], // Slate 50
  lighter: [241, 245, 249], // Slate 100
  dark: [30, 41, 59], // Slate 800
  darker: [15, 23, 42], // Slate 900
  text: [33, 33, 33], // Soft Black
  muted: [100, 100, 100], // Gray
  border: [220, 220, 220], // Light Gray
};

// Professional typography scale
const FONTS = {
  title: 24,
  subtitle: 18,
  heading1: 16,
  heading2: 14,
  heading3: 12,
  body: 10,
  small: 8,
  tiny: 7,
};

// Layout constants
const LAYOUT = {
  pageWidth: 210, // A4 width in mm
  pageHeight: 297, // A4 height in mm
  margin: 20,
  headerHeight: 25,
  footerHeight: 15,
  lineHeight: 4, // Optimized for professional density
  sectionSpacing: 6, // Reduced from 8
  paragraphSpacing: 4, // Reduced from 6
};

// Enhanced EBIOS RM logo as SVG data URL
const EBIOS_RM_LOGO_BASE64 =
  "data:image/svg+xml;base64," +
  btoa(`
<svg width="80" height="40" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#2563eb;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#1d4ed8;stop-opacity:1" />
    </linearGradient>
  </defs>
  <rect width="80" height="40" fill="url(#logoGrad)" rx="6"/>
  <text x="40" y="22" text-anchor="middle" fill="white" font-family="Arial,Helvetica,sans-serif" font-size="11" font-weight="bold">EBIOS RM</text>
  <text x="40" y="33" text-anchor="middle" fill="white" font-family="Arial,Helvetica,sans-serif" font-size="8" font-weight="normal">Analyse de Risques</text>
</svg>
`);

export class EnhancedPDFGenerator {
  constructor(options = {}) {
    this.pdf = new jsPDF("p", "mm", "a4");
    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
    this.margin = LAYOUT.margin;
    this.currentY = this.margin;
    this.lineHeight = LAYOUT.lineHeight;

    // Document metadata
    this.metadata = {
      title: options.title || "Rapport EBIOS RM",
      subject: options.subject || "Analyse de risques cybers\u00e9curit\u00e9",
      author: options.author || "EBIOS RM System",
      creator: "EBIOS RM Enhanced Report Generator",
      keywords:
        options.keywords ||
        "EBIOS RM, cybers\u00e9curit\u00e9, analyse de risques, ANSSI",
    };

    // Store options for report generation
    this.options = options;

    // Set PDF metadata
    this.pdf.setProperties(this.metadata);

    // Page numbering
    this.pageNumber = 1;
    this.totalPages = 0;

    // Table of contents
    this.tableOfContents = [];
    this.currentSection = "";

    // Logo data storage
    this.logos = {};

    // Table of contents page tracking
    this.tocPageNumber = null;
    this.tocPagesReserved = 0;
    this.tocPagesActual = 0;
  }

  /**
   * Add a professional EBIOS RM cover page
   * @param {Object} reportData - Optional report data for personalization
   */
  addEbiosrmCoverPage(reportData = {}) {
    const doc = this.pdf;
    const colors = COLORS;

    // Professional typography
    const fonts = {
      header: 10,
      title: 24,
      subtitle: 16,
      body: 10,
      small: 8,
      tiny: 7,
    };

    const pageWidth = this.pageWidth;
    const pageHeight = this.pageHeight;
    const margin = this.margin;
    const centerX = pageWidth / 2;

    const contextInfo = (reportData.executiveSummary || {}).contextInfo || {};
    const metadata = reportData.metadata || {};

    // Clean white background
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    let currentY = 30;

    // Add EBIOS RM logo at the top center
    try {
      doc.addImage(EBIOS_RM_LOGO_BASE64, "PNG", centerX - 40, currentY, 80, 40);
      currentY += 50;
    } catch (error) {
      console.warn("Logo loading failed, continuing without logo");
      currentY += 30;
    }

    // Header section with enhanced styling
    doc.setFillColor(...colors.light);
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, currentY, pageWidth - 2 * margin, 12, 2, 2, "FD");

    // Programme info with better typography
    doc.setFontSize(fonts.header);
    doc.setTextColor(...colors.primary);
    doc.setFont("helvetica", "bold");
    doc.text("Programme :", margin + 3, currentY + 7);
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "normal");
    doc.text(
      "EBIOS RM - Analyse de Risques Cybers\u00e9curit\u00e9",
      margin + 35,
      currentY + 7,
    );

    currentY += 18;

    // Document info section
    doc.setFillColor(...colors.light);
    doc.roundedRect(margin, currentY, pageWidth - 2 * margin, 10, 2, 2, "FD");

    // Document type
    doc.setFontSize(fonts.body);
    doc.setTextColor(...colors.primary);
    doc.setFont("helvetica", "bold");
    doc.text("Document :", margin + 3, currentY + 6);
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "normal");
    const docType = reportData.isMatrix
      ? "Rapport Matrice d'Analyse"
      : "Plan de Traitement des Risques";
    doc.text(docType, margin + 30, currentY + 6);

    currentY += 15;

    // Version and Reference with improved layout
    const infoBoxWidth = (pageWidth - 3 * margin) / 2;

    // Version box
    doc.setFillColor(...colors.light);
    doc.roundedRect(margin, currentY, infoBoxWidth, 10, 2, 2, "FD");
    doc.setFontSize(fonts.body);
    doc.setTextColor(...colors.primary);
    doc.setFont("helvetica", "bold");
    doc.text("Version :", margin + 3, currentY + 6);
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "normal");
    doc.text(metadata.version || "1.0", margin + 25, currentY + 6);

    // Reference box
    doc.setFillColor(...colors.light);
    doc.roundedRect(
      margin + infoBoxWidth + margin,
      currentY,
      infoBoxWidth,
      10,
      2,
      2,
      "FD",
    );
    doc.setFontSize(fonts.body);
    doc.setTextColor(...colors.primary);
    doc.setFont("helvetica", "bold");
    doc.text(
      "R\u00e9f\u00e9rence :",
      margin + infoBoxWidth + margin + 3,
      currentY + 6,
    );
    doc.setTextColor(...colors.text);
    doc.setFont("helvetica", "normal");
    doc.text(
      metadata.reference || "ACG_EBIOSRM_MATRICE",
      margin + infoBoxWidth + margin + 32,
      currentY + 6,
    );

    currentY += 35;

    // Classification badge (Internal)
    doc.setFillColor(200, 200, 200);
    doc.setDrawColor(50, 50, 50);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    const badgeText = "Interne";
    const badgeWidth = doc.getTextWidth(badgeText) + 10;
    doc.roundedRect(
      pageWidth - margin - badgeWidth,
      currentY - 20,
      badgeWidth,
      8,
      2,
      2,
      "FD",
    );
    doc.text(badgeText, pageWidth - margin - badgeWidth / 2, currentY - 16, {
      align: "center",
    });
    doc.setTextColor(...colors.text);

    // Main title with enhanced styling
    const maxTitleWidth = pageWidth - 2 * margin;
    const titleLine1 = contextInfo.organizationName || "Rapport de Matrice";
    const titleLine2 = contextInfo.analysisName || "EBIOS RM";
    const tlh = 7;
    let ty = currentY;
    doc.setFontSize(fonts.title);
    doc.setTextColor(...colors.primary);
    doc.setFont("helvetica", "bold");
    const l1 = this.pdf.splitTextToSize(titleLine1, maxTitleWidth);
    l1.forEach((line, i) => doc.text(line, centerX, ty + i * tlh, { align: 'center' }));
    ty += l1.length * tlh + 2;
    const l2 = this.pdf.splitTextToSize(titleLine2, maxTitleWidth);
    l2.forEach((line, i) => doc.text(line, centerX, ty + i * tlh, { align: 'center' }));
    ty += l2.length * tlh;
    currentY = ty + 12;

    // EBIOSRM subtitle
    doc.setFontSize(fonts.subtitle);
    doc.setTextColor(...colors.secondary);
    doc.setFont("helvetica", "bold");
    doc.text("M\u00c9THODOLOGIE EBIOS RM", centerX, currentY, {
      align: "center",
    });

    currentY += 35;

    // Enhanced information table
    const tableY = currentY;
    const rowHeight = 12;
    const tableWidth = pageWidth - 2 * margin;

    // Table data
    const tableData = [
      [
        "Organisation",
        contextInfo.organizationName ||
          metadata.companyName ||
          "Non sp\u00e9cifi\u00e9e",
      ],
      ["P\u00e9rim\u00e8tre", contextInfo.scope || "Analyse Compl\u00e8te"],
      ["Classification", "Interne"],
      ["Auteur", metadata.createdBy || "Syst\u00e8me EBIOS RM"],
      ["Statut", metadata.status || "En cours de validation"],
      ["Date de g\u00e9n\u00e9ration", new Date().toLocaleDateString("fr-FR")],
    ];

    // Draw table with enhanced styling
    tableData.forEach((row, index) => {
      const y = tableY + index * rowHeight;

      // Label column (blue background)
      doc.setFillColor(...colors.primary);
      doc.roundedRect(margin, y, tableWidth * 0.35, rowHeight, 1, 1, "FD");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(fonts.small);
      doc.setFont("helvetica", "bold");
      doc.text(row[0], margin + 3, y + 7);

      // Value column (light background)
      doc.setFillColor(...colors.light);
      doc.roundedRect(
        margin + tableWidth * 0.35,
        y,
        tableWidth * 0.65,
        rowHeight,
        1,
        1,
        "FD",
      );
      doc.setTextColor(...colors.text);
      doc.setFont("helvetica", "normal");
      doc.text(row[1], margin + tableWidth * 0.35 + 3, y + 7);
    });

    // Footer section
    const footerY = pageHeight - 30;

    // Footer line
    doc.setDrawColor(...colors.muted);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

    // Footer text
    doc.setFontSize(fonts.small);
    doc.setTextColor(...colors.muted);
    doc.setFont("helvetica", "normal");
    doc.text(
      "Syst\u00e8me d'analyse de risques - Conforme EBIOS RM",
      centerX,
      footerY,
      { align: "center" },
    );

    doc.setFontSize(fonts.tiny);
    doc.text(
      "Document confidentiel - G\u00e9n\u00e9r\u00e9 automatiquement",
      centerX,
      footerY + 8,
      { align: "center" },
    );

    // Add watermark
    this.addWatermark();
  }

  /**
   * Set logo data for the document
   * @param {Object} logoData - Logo data from logoService
   */
  setLogos(logoData) {
    this.logos = logoData;
  }

  /**
   * Generate professional cover page
   * @param {Object} reportData - Report metadata
   */
  generateCoverPage(reportData) {
    if (this.options.workshop5Only) {
      this.generateWorkshop5CoverPage(reportData);
      return;
    }

    // Default cover page for complete reports
    this.generateDefaultCoverPage(reportData);
  }

  /**
   * Generate executive summary
   * @param {Object} reportData - Report data
   */
  generateExecutiveSummary(reportData) {
    this.addSectionHeading("1. SYNTH\u00c8SE EX\u00c9CUTIVE", 1);
    this.addSpace(1);

    const metadata = reportData.metadata || {};
    const summary = reportData.executiveSummary || {};
    const contextInfo = summary.contextInfo || {};
    const metrics = summary.metrics || {};

    this.addParagraph(
      `Cette synth\u00e8se pr\u00e9sente les r\u00e9sultats principaux de l'analyse EBIOS RM men\u00e9e pour ${contextInfo.organizationName || "l'organisation"}. ` +
        `L'analyse a \u00e9t\u00e9 conduite selon la m\u00e9thodologie EBIOS RM (Expression des Besoins et Identification des Objectifs de S\u00e9curit\u00e9 - Risk Manager) ` +
        `d\u00e9velopp\u00e9e par l'ANSSI, permettant une approche structur\u00e9e et syst\u00e9matique de l'\u00e9valuation des risques cybers\u00e9curit\u00e9.`,
    );

    this.addSpace(8);

    this.addSectionHeading("CONTEXTE DE L'ANALYSE", 2);

    this.addParagraph(
      `Cette section pr\u00e9sente le contexte g\u00e9n\u00e9ral dans lequel s'inscrit l'analyse de risques. Elle d\u00e9finit les \u00e9l\u00e9ments ` +
        `fondamentaux qui ont guid\u00e9 la d\u00e9marche d'\u00e9valuation et les objectifs poursuivis.`,
    );

    this.addParagraph(
      `• Titre: ${contextInfo.analysisName || "Analyse EBIOS RM"}`,
    );
    this.addParagraph(`• Organisation: ${contextInfo.organizationName || "-"}`);
    this.addParagraph(
      `• Date de g\u00e9n\u00e9ration: ${new Date().toLocaleDateString("fr-FR")}`,
    );
    this.addParagraph(`• Version: ${metadata.version || "1.0"}`);
    this.addParagraph(`• P\u00e9rim\u00e8tre: ${contextInfo.scope || "-"}`);
    this.addParagraph(
      `• Objectif: ${contextInfo.analysisObjective || "Analyse de risques selon la m\u00e9thode EBIOS RM"}`,
    );

    this.addSpace(10);

    this.addSectionHeading("M\u00c9TRIQUES CL\u00c9S", 2);

    this.addParagraph(
      `Les m\u00e9triques suivantes fournissent une vue d'ensemble quantitative des \u00e9l\u00e9ments analys\u00e9s durant l'\u00e9tude EBIOS RM. ` +
        `Ces indicateurs permettent d'appr\u00e9hender l'ampleur et la complexit\u00e9 de l'analyse men\u00e9e.`,
    );

    this.addSpace(5);

    // Create metrics table data
    const metricsHeaders = ["M\u00e9trique", "Valeur"];
    const metricsRows = [
      [
        "Valeurs m\u00e9tier identifi\u00e9es",
        (metrics.totalBusinessValues || 0).toString(),
      ],
      [
        "\u00c9v\u00e9nements redout\u00e9s analys\u00e9s",
        (metrics.totalDreadedEvents || 0).toString(),
      ],
      [
        "Sources de risque \u00e9valu\u00e9es",
        (metrics.totalRiskSources || 0).toString(),
      ],
      [
        "Sc\u00e9narios strat\u00e9giques mod\u00e9lis\u00e9s",
        (metrics.totalStrategicScenarios || 0).toString(),
      ],
      [
        "Sc\u00e9narios op\u00e9rationnels d\u00e9taill\u00e9s",
        (metrics.totalOperationalScenarios || 0).toString(),
      ],
      [
        "Plans de traitement d\u00e9finis",
        (metrics.totalTreatmentPlans || 0).toString(),
      ],
      [
        "Contr\u00f4les de s\u00e9curit\u00e9 recens\u00e9s",
        (metrics.totalSecurityControls || 0).toString(),
      ],
    ];

    // Add metrics table with professional styling
    this.addTable(metricsHeaders, metricsRows, {
      columnWidths: [80, 20],
      alternateRows: true,
      fontSize: FONTS.small,
    });

    this.addSpace(5);
  }

  /**
   * Generate comprehensive framing and security baseline section
   * @param {Object} reportData - Report data
   */
  generateFramingAndBaseline(reportData) {
    this.addSectionHeading("2. CADRAGE ET SOCLE DE S\u00c9CURIT\u00c9", 1);
    this.addSpace(10);

    this.addParagraph(
      `Cette section pr\u00e9sente le cadrage de l'\u00e9tude EBIOS RM ainsi que le socle de s\u00e9curit\u00e9 existant. ` +
        `Elle constitue la base fondamentale sur laquelle s'appuie l'ensemble de l'analyse de risques, ` +
        `d\u00e9finissant le p\u00e9rim\u00e8tre d'\u00e9tude, les valeurs m\u00e9tier \u00e0 prot\u00e9ger et les mesures de s\u00e9curit\u00e9 d\u00e9j\u00e0 en place.`,
    );

    this.addSpace(10);

    // 2.1 Study Framework
    this.generateStudyFramework(reportData);

    // 2.2 Business and Technical Scope
    this.generateBusinessTechnicalScope(reportData);

    // 2.3 Dreaded Events Identification
    this.generateDreadedEventsSection(reportData);

    // 2.4 Security Baseline
    this.generateSecurityBaseline(reportData);
  }

  /**
   * Generate study framework subsection
   * @param {Object} reportData - Report data
   */
  generateStudyFramework(reportData) {
    this.addSectionHeading("2.1. CADRE DE L'\u00c9TUDE", 2);

    this.addParagraph(
      `Le cadre de l'\u00e9tude d\u00e9finit les objectifs, la m\u00e9thodologie et les contraintes de l'analyse EBIOS RM. ` +
        `Il \u00e9tablit les bases m\u00e9thodologiques et organisationnelles n\u00e9cessaires \u00e0 la conduite de l'\u00e9valuation des risques.`,
    );

    this.addSpace(5);

    const contextInfo = (reportData.executiveSummary || {}).contextInfo || {};
    const metadata = reportData.metadata || {};

    // Framework information table
    const frameworkHeaders = ["\u00c9l\u00e9ment", "Description"];
    const frameworkRows = [
      [
        "M\u00e9thodologie",
        "EBIOS RM (ANSSI) - Expression des Besoins et Identification des Objectifs de S\u00e9curit\u00e9",
      ],
      ["R\u00e9f\u00e9rentiel", "Guide EBIOS RM v1.0 - ANSSI"],
      [
        "P\u00e9rim\u00e8tre organisationnel",
        contextInfo.organizationName || "\u00c0 d\u00e9finir",
      ],
      [
        "Objectif principal",
        contextInfo.analysisObjective ||
          "\u00c9valuation des risques cybers\u00e9curit\u00e9",
      ],
      [
        "Dur\u00e9e de l'\u00e9tude",
        metadata.studyDuration || "Non sp\u00e9cifi\u00e9e",
      ],
      [
        "\u00c9quipe projet",
        metadata.projectTeam || "\u00c9quipe s\u00e9curit\u00e9 interne",
      ],
      ["Niveau de confidentialit\u00e9", "Interne - Usage restreint"],
    ];

    this.addTable(frameworkHeaders, frameworkRows, {
      columnWidths: [50, 90],
      alternateRows: true,
      fontSize: FONTS.small,
    });

    this.addSpace(10);

    this.addInfoBox(
      "M\u00e9thodologie EBIOS RM",
      "EBIOS RM est une m\u00e9thode d'analyse et de gestion des risques num\u00e9riques d\u00e9velopp\u00e9e par l'ANSSI. " +
        "Elle permet d'identifier et d'\u00e9valuer les risques pesant sur les missions d'un organisme, " +
        "puis de d\u00e9terminer les mesures de s\u00e9curit\u00e9 \u00e0 mettre en \u0153uvre pour les traiter.",
      { type: "info" },
    );

    this.addSpace(10);
  }

  /**
   * Generate business and technical scope subsection
   * @param {Object} reportData - Report data
   */
  generateBusinessTechnicalScope(reportData) {
    this.addSectionHeading(
      "2.2. P\u00c9RIM\u00c8TRE M\u00c9TIER ET TECHNIQUE",
      2,
    );

    this.addParagraph(
      `La d\u00e9finition du p\u00e9rim\u00e8tre constitue une \u00e9tape cruciale de l'analyse EBIOS RM. Elle d\u00e9limite pr\u00e9cis\u00e9ment ` +
        `les \u00e9l\u00e9ments m\u00e9tier et techniques qui seront pris en compte dans l'\u00e9valuation des risques, ` +
        `garantissant ainsi la pertinence et l'exhaustivit\u00e9 de l'analyse.`,
    );

    this.addSpace(8);

    // 2.2.3 Valeurs m\u00e9tier
    this.addSectionHeading("2.2.3. VALEURS M\u00c9TIER", 3);

    this.addParagraph(
      `Les valeurs m\u00e9tier constituent le c\u0153ur de l'organisation et repr\u00e9sentent les \u00e9l\u00e9ments cruciaux ` +
        `qui doivent \u00eatre prot\u00e9g\u00e9s contre les risques identifi\u00e9s. Cette section pr\u00e9sente l'inventaire ` +
        `des valeurs m\u00e9tier prioritaires ainsi que leur niveau de criticit\u00e9 pour l'organisation.`,
    );

    this.addSpace(8);

    // Business scope table
    const businessValues = reportData.businessValues || [];
    if (businessValues.length > 0) {
      const businessHeaders = [
        "Valeur M\u00e9tier",
        "Description",
        "Criticit\u00e9",
        "Propri\u00e9taire",
      ];
      const businessRows = businessValues.map((value) => [
        value.name || "-",
        value.description || "Description non disponible",
        this.formatCriticality(value.criticality),
        value.owner || "-",
      ]);

      this.addTable(businessHeaders, businessRows, {
        columnWidths: [35, 60, 20, 25],
        alternateRows: true,
        fontSize: FONTS.small,
      });
    } else {
      this.addParagraph(
        "Aucune valeur m\u00e9tier n'a \u00e9t\u00e9 d\u00e9finie dans le p\u00e9rim\u00e8tre de cette analyse.",
      );
    }

    this.addSpace(15);

    // 2.2.4 Biens supports
    this.addSectionHeading("2.2.4. BIENS SUPPORTS", 3);

    this.addParagraph(
      `Les biens supports d\u00e9signent l'ensemble des composants techniques, syst\u00e8mes informatiques, ` +
        `r\u00e9seaux et infrastructures qui permettent la r\u00e9alisation des missions m\u00e9tier. Cette section ` +
        `pr\u00e9sente la cartographie d\u00e9taill\u00e9e des actifs techniques et leur niveau de criticit\u00e9.`,
    );

    this.addSpace(8);

    // Extract support assets from business values
    const allSupportAssets = (reportData.businessValues || []).flatMap((bv) =>
      (bv.supportAssets || []).map((asset) => ({
        ...asset,
        businessValueName: bv.name || "-",
      })),
    );

    if (allSupportAssets.length > 0) {
      this.addParagraph(
        `${allSupportAssets.length} bien${allSupportAssets.length > 1 ? "s" : ""} support${allSupportAssets.length > 1 ? "s" : ""} identifi\u00e9${allSupportAssets.length > 1 ? "s" : ""}.`,
      );
      this.addSpace(5);

      const assetTypeLabels = {
        organisation: "Organisation",
        locaux: "Locaux",
        equipements: "\u00c9quipements",
        reseaux: "R\u00e9seaux",
        logiciels: "Logiciels",
      };

      const techHeaders = [
        "ID",
        "Nom",
        "Type",
        "Propri\u00e9taire",
        "Localisation",
        "Fournisseur/Produit",
        "Version",
      ];
      const techRows = allSupportAssets.map((asset) => {
        let vendorProduct = "-";
        if (asset.vendor && asset.product) {
          vendorProduct = `${asset.vendor} / ${asset.product}`;
        } else if (asset.vendor) {
          vendorProduct = asset.vendor;
        } else if (asset.product) {
          vendorProduct = asset.product;
        }
        const version = asset.version ? `v${asset.version}` : "-";
        return [
          asset.shortId || "N/A",
          asset.name || "Sans nom",
          assetTypeLabels[asset.type] || asset.type || "-",
          asset.owner || "-",
          asset.location || "-",
          vendorProduct,
          version,
        ];
      });

      this.addTable(techHeaders, techRows, {
        columnWidths: [15, 30, 22, 22, 22, "auto", 15],
        headStyles: {
          fillColor: [34, 197, 94],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        styles: {
          lineColor: [226, 232, 240],
          lineWidth: 0.5,
        },
        columnStyles: {
          0: { halign: "center" },
          2: { halign: "center" },
          6: { halign: "center" },
        },
        fontSize: FONTS.small,
      });

      // Asset type distribution summary
      this.addSpace(10);
      const assetTypeStats = {};
      allSupportAssets.forEach((asset) => {
        const typeKey = asset.type || "Non d\u00e9fini";
        const typeLabel = assetTypeLabels[typeKey] || typeKey;
        assetTypeStats[typeLabel] = (assetTypeStats[typeLabel] || 0) + 1;
      });
      this.addParagraph("R\u00e9partition par type d'actif :");
      Object.entries(assetTypeStats).forEach(([type, count]) => {
        const percentage = Math.round((count / allSupportAssets.length) * 100);
        this.addParagraph(`  \u2022 ${type} : ${count} (${percentage}%)`);
      });
    } else {
      this.addParagraph(
        "Aucun bien support n'a \u00e9t\u00e9 identifi\u00e9 dans le p\u00e9rim\u00e8tre de cette analyse.",
      );
    }

    this.addSpace(15);
  }

  /**
   * Generate dreaded events identification subsection
   * @param {Object} reportData - Report data
   */
  generateDreadedEventsSection(reportData) {
    this.addSectionHeading(
      "2.3. IDENTIFICATION DES \u00c9V\u00c9NEMENTS REDOUT\u00c9S",
      2,
    );

    this.addParagraph(
      `Les \u00e9v\u00e9nements redout\u00e9s repr\u00e9sentent les situations que l'organisation souhaite \u00e9viter \u00e0 tout prix. ` +
        `Ils correspondent aux impacts n\u00e9gatifs sur les valeurs m\u00e9tier identifi\u00e9es et constituent le point de d\u00e9part ` +
        `de l'analyse des risques. Chaque \u00e9v\u00e9nement redout\u00e9 est caract\u00e9ris\u00e9 par son impact sur les crit\u00e8res ` +
        `de s\u00e9curit\u00e9 : Disponibilit\u00e9, Int\u00e9grit\u00e9, Confidentialit\u00e9 et Preuve.`,
    );

    this.addSpace(8);

    const dreadedEvents = reportData.dreadedEvents || [];
    if (dreadedEvents.length > 0) {
      this.addParagraph(
        `Au total, ${dreadedEvents.length} \u00e9v\u00e9nement(s) redout\u00e9(s) ont \u00e9t\u00e9 identifi\u00e9(s) dans le cadre de cette analyse. ` +
          `Le tableau suivant pr\u00e9sente ces \u00e9v\u00e9nements avec leur niveau de gravit\u00e9 associ\u00e9.`,
      );

      this.addSpace(5);

      const eventsHeaders = [
        "\u00c9v\u00e9nement Redout\u00e9",
        "Valeur M\u00e9tier Impact\u00e9e",
        "Gravit\u00e9",
        "Crit\u00e8res DICP",
      ];
      const eventsRows = dreadedEvents.map((event) => [
        event.name || "Non d\u00e9fini",
        event.businessValue || "Non sp\u00e9cifi\u00e9e",
        this.formatSeverity(event.severity),
        this.formatDICPCriteria(event.dicpCriteria),
      ]);

      this.addTable(eventsHeaders, eventsRows, {
        columnWidths: [45, 35, 20, 40],
        alternateRows: true,
        fontSize: FONTS.small,
      });
    } else {
      this.addParagraph(
        "Aucun \u00e9v\u00e9nement redout\u00e9 n'a \u00e9t\u00e9 d\u00e9fini dans cette analyse.",
      );
    }

    this.addSpace(8);

    this.addInfoBox(
      "Crit\u00e8res DICP",
      "D = Disponibilit\u00e9 : capacit\u00e9 d'acc\u00e8s aux informations et syst\u00e8mes quand n\u00e9cessaire. " +
        "I = Int\u00e9grit\u00e9 : exactitude et compl\u00e9tude des informations. " +
        "C = Confidentialit\u00e9 : protection contre la divulgation non autoris\u00e9e. " +
        "P = Preuve : capacit\u00e9 \u00e0 prouver qu'un \u00e9v\u00e9nement a eu lieu.",
      { type: "info" },
    );

    this.addSpace(10);
  }

  /**
   * Generate security baseline subsection
   * @param {Object} reportData - Report data
   */
  generateSecurityBaseline(reportData) {
    this.addSectionHeading("2.4. SOCLE DE S\u00c9CURIT\u00c9", 2);

    this.addParagraph(
      `Le socle de s\u00e9curit\u00e9 recense l'ensemble des mesures de s\u00e9curit\u00e9 d\u00e9j\u00e0 mises en \u0153uvre par l'organisation. ` +
        `Cette cartographie des contr\u00f4les existants permet d'\u00e9valuer le niveau de maturit\u00e9 s\u00e9curitaire actuel ` +
        `et d'identifier les lacunes \u00e0 combler. Le socle est organis\u00e9 selon les domaines de s\u00e9curit\u00e9 ` +
        `et r\u00e9f\u00e9rentiels reconnus (ISO 27001, NIST, ANSSI, etc.).`,
    );

    this.addSpace(8);

    const securityControls = reportData.securityControls || [];
    if (securityControls.length > 0) {
      this.addParagraph(
        `L'organisation dispose actuellement de ${securityControls.length} mesure(s) de s\u00e9curit\u00e9 identifi\u00e9e(s). ` +
          `Le tableau suivant pr\u00e9sente ces mesures organis\u00e9es par domaine de s\u00e9curit\u00e9.`,
      );

      this.addSpace(5);

      // Group controls by domain
      const controlsByDomain = this.groupControlsByDomain(securityControls);

      Object.keys(controlsByDomain).forEach((domain) => {
        this.addSectionHeading(`DOMAINE : ${domain.toUpperCase()}`, 3);

        const domainControls = controlsByDomain[domain];
        const controlHeaders = [
          "Mesure de S\u00e9curit\u00e9",
          "Statut",
          "Efficacit\u00e9",
          "Responsable",
        ];
        const controlRows = domainControls.map((control) => [
          control.name || "Non d\u00e9finie",
          this.formatControlStatus(control.status),
          this.formatEffectiveness(control.effectiveness),
          control.owner || "Non assign\u00e9",
        ]);

        this.addTable(controlHeaders, controlRows, {
          columnWidths: [50, 25, 25, 30],
          alternateRows: true,
          fontSize: FONTS.small,
        });

        this.addSpace(8);
      });
    } else {
      this.addParagraph(
        "Aucune mesure de s\u00e9curit\u00e9 n'a \u00e9t\u00e9 recens\u00e9e dans le socle actuel.",
      );

      this.addInfoBox(
        "Recommandation",
        "Il est fortement recommand\u00e9 de proc\u00e9der \u00e0 un inventaire complet des mesures de s\u00e9curit\u00e9 existantes " +
          "avant de poursuivre l'analyse EBIOS RM. Cette cartographie est essentielle pour \u00e9valuer " +
          "correctement les risques r\u00e9siduels.",
        { type: "warning" },
      );
    }

    this.addSpace(10);

    // Add reference links
    this.addSectionHeading("R\u00c9F\u00c9RENCES ET STANDARDS", 3);

    this.addParagraph(
      `Les mesures de s\u00e9curit\u00e9 du socle s'appuient sur les r\u00e9f\u00e9rentiels et standards suivants :`,
    );

    const references = [
      "• ISO/IEC 27001:2022 - Syst\u00e8mes de management de la s\u00e9curit\u00e9 de l'information",
      "• NIST Cybersecurity Framework v1.1",
      "• Guide d'hygi\u00e8ne informatique - ANSSI",
      "• R\u00e9f\u00e9rentiel G\u00e9n\u00e9ral de S\u00e9curit\u00e9 (RGS) v2.0",
      "• OWASP Top 10 - Risques de s\u00e9curit\u00e9 des applications web",
      "• CIS Controls v8 - Center for Internet Security",
    ];

    references.forEach((ref) => {
      this.addParagraph(ref);
    });

    this.addSpace(15);
  }

  /**
   * Helper method to format criticality levels
   * @param {string} criticality - Criticality level
   * @returns {string} Formatted criticality
   */
  formatCriticality(criticality) {
    const levels = {
      critical: "Critique",
      high: "\u00c9lev\u00e9e",
      medium: "Moyenne",
      low: "Faible",
    };
    return levels[criticality] || "Non d\u00e9finie";
  }

  /**
   * Helper method to format severity levels
   * @param {string} severity - Severity level
   * @returns {string} Formatted severity
   */
  formatSeverity(severity) {
    const levels = {
      critical: "Critique",
      high: "\u00c9lev\u00e9e",
      medium: "Moyenne",
      low: "Faible",
    };
    return levels[severity] || "Non d\u00e9finie";
  }

  /**
   * Helper method to format DICP criteria
   * @param {Object} criteria - DICP criteria object
   * @returns {string} Formatted criteria
   */
  formatDICPCriteria(criteria) {
    if (!criteria) return "Non d\u00e9fini";

    const impacts = [];
    if (criteria.disponibilite) impacts.push("D");
    if (criteria.integrite) impacts.push("I");
    if (criteria.confidentialite) impacts.push("C");
    if (criteria.preuve) impacts.push("P");

    return impacts.length > 0 ? impacts.join(", ") : "Aucun";
  }

  /**
   * Helper method to format control status
   * @param {string} status - Control status
   * @returns {string} Formatted status
   */
  formatControlStatus(status) {
    const statuses = {
      implemented: "Impl\u00e9ment\u00e9e",
      partial: "Partielle",
      planned: "Planifi\u00e9e",
      not_implemented: "Non impl\u00e9ment\u00e9e",
    };
    return statuses[status] || "Non d\u00e9fini";
  }

  /**
   * Helper method to format effectiveness levels
   * @param {string} effectiveness - Effectiveness level
   * @returns {string} Formatted effectiveness
   */
  formatEffectiveness(effectiveness) {
    const levels = {
      high: "\u00c9lev\u00e9e",
      medium: "Moyenne",
      low: "Faible",
      unknown: "Inconnue",
    };
    return levels[effectiveness] || "Non \u00e9valu\u00e9e";
  }

  /**
   * Helper method to group security controls by domain
   * @param {Array} controls - Security controls array
   * @returns {Object} Controls grouped by domain
   */
  groupControlsByDomain(controls) {
    const grouped = {};

    controls.forEach((control) => {
      const domain = control.domain || "Autres";
      if (!grouped[domain]) {
        grouped[domain] = [];
      }
      grouped[domain].push(control);
    });

    return grouped;
  }

  /**
   * Generate clean, professional Workshop 5 cover page
   * @param {Object} reportData - Report metadata
   */
  generateWorkshop5CoverPage(reportData) {
    // Clean white background
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.rect(0, 0, this.pageWidth, this.pageHeight, "F");

    // Add watermark to cover page FIRST (behind all content)
    console.log("Adding watermark to Workshop 5 cover page");
    this.addWatermark();

    const centerX = this.pageWidth / 2;
    let currentY = 60;

    // Add EBIOS RM logo at the top using hardcoded base64
    try {
      console.log("Adding hardcoded EBIOS RM logo to Workshop 5 cover page");
      // Position logo centered at the top
      const logoWidth = 80; // Reasonable size for cover page
      const logoHeight = 40;
      const logoX = centerX - logoWidth / 2;
      const logoY = 20;

      // Use hardcoded base64 logo
      this.pdf.addImage(
        EBIOS_RM_LOGO_BASE64,
        "PNG",
        logoX,
        logoY,
        logoWidth,
        logoHeight,
      );
      console.log("EBIOS RM logo added successfully to Workshop 5 cover page");
      currentY = 75; // Adjust starting position for title
    } catch (error) {
      console.error(
        "Failed to add hardcoded EBIOS RM logo to Workshop 5 cover page:",
        error,
      );
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
      });
      // Still reserve space even if logo fails
      currentY = 75;
    }

    // Simple header line
    this.pdf.setDrawColor(...COLORS.primary);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(
      this.margin,
      currentY - 15,
      this.pageWidth - this.margin,
      currentY - 15,
    );

    // Main title with enhanced styling
    this.pdf.setFontSize(24);
    this.pdf.setTextColor(...COLORS.primary);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("RAPPORT EBIOS RM", centerX, currentY, { align: "center" });

    currentY += 18;

    // Workshop 5 identifier with distinctive color
    this.pdf.setFontSize(16);
    this.pdf.setTextColor(...COLORS.teal);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("Atelier 5 - Traitement du Risque", centerX, currentY, {
      align: "center",
    });

    currentY += 40;

    // Company/Organization name with enhanced styling
    const contextInfo = (reportData.executiveSummary || {}).contextInfo || {};
    const maxTW = this.pageWidth - 2 * this.margin;
    if (contextInfo.organizationName) {
      this.pdf.setFontSize(18);
      this.pdf.setTextColor(...COLORS.dark);
      this.pdf.setFont("helvetica", "bold");
      const ol = this.pdf.splitTextToSize(contextInfo.organizationName, maxTW);
      ol.forEach((line, i) => this.pdf.text(line, centerX, currentY + i * 7, { align: 'center' }));
      currentY += ol.length * 7 + 4;
    }

    // Analysis name with distinctive color
    if (contextInfo.analysisName) {
      this.pdf.setFontSize(14);
      this.pdf.setTextColor(...COLORS.orange);
      this.pdf.setFont("helvetica", "bold");
      const al = this.pdf.splitTextToSize(contextInfo.analysisName, maxTW);
      al.forEach((line, i) => this.pdf.text(line, centerX, currentY + i * 6, { align: 'center' }));
      currentY += al.length * 6 + 4;
    }

    // Simple information box
    const boxY = currentY + 20;
    const boxWidth = 140;
    const boxHeight = 60;
    const boxX = (this.pageWidth - boxWidth) / 2;

    // Clean box with subtle border
    this.pdf.setFillColor(250, 250, 250);
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.rect(boxX, boxY, boxWidth, boxHeight, "FD");

    // Box content
    let infoY = boxY + 15;
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(...COLORS.text);
    this.pdf.setFont("helvetica", "normal");

    const metadata = reportData.metadata || {};

    const infoItems = [
      `Version: ${metadata.version || "1.0"}`,
      `Auteur: ${metadata.createdBy || "Syst\u00e8me EBIOS RM"}`,
      `Statut: ${metadata.status || "Finalis\u00e9"}`,
      `Date: ${new Date().toLocaleDateString("fr-FR")}`,
    ];

    infoItems.forEach((item) => {
      this.pdf.text(item, boxX + 10, infoY, { align: "left" });
      infoY += 10;
    });

    // Simple footer
    const footerY = this.pageHeight - 40;

    // Footer line
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(
      this.margin,
      footerY - 10,
      this.pageWidth - this.margin,
      footerY - 10,
    );

    // Footer text
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(
      "M\u00e9thodologie EBIOS RM - Analyse de Risques Cybers\u00e9curit\u00e9",
      centerX,
      footerY,
      { align: "center" },
    );

    this.pdf.setFontSize(8);
    this.pdf.text(
      "Document confidentiel - Usage interne uniquement",
      centerX,
      footerY + 10,
      { align: "center" },
    );

    // Cover page complete - caller should call generateTableOfContents() next
  }

  /**
   * Generate default cover page for complete reports using professional EBIOS RM template
   * @param {Object} reportData - Report metadata
   */
  generateDefaultCoverPage(reportData) {
    // Clean white background
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.rect(0, 0, this.pageWidth, this.pageHeight, "F");

    const contextInfo = (reportData.executiveSummary || {}).contextInfo || {};
    const metadata = reportData.metadata || {};

    // Header table - Programme info
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.5);
    this.pdf.rect(15, 15, 180, 10, "S");

    // Add logo

    this.pdf.setFontSize(9);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("Programme :", 17, 21);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text("Analyse des risques – EBIOS RM", 45, 21);

    // Internal classification label
    this.pdf.setFillColor(200, 200, 200); // Gray background instead of light orange
    this.pdf.rect(135, 15, 60, 10, "FD");
    this.pdf.setTextColor(0, 0, 0); // Black text instead of orange
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("Interne", 155, 21);
    this.pdf.setTextColor(0, 0, 0);

    // Document type
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.rect(15, 25, 180, 8, "S");
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("Document :", 17, 30);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text("Rapport Complet EBIOS RM", 45, 30);

    // Version and Reference
    this.pdf.rect(15, 33, 90, 8, "S");
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("Version :", 17, 38);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(metadata.version || "1.0", 37, 38);

    this.pdf.rect(105, 33, 90, 8, "S");
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("R\u00e9f :", 107, 38);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(metadata.reference || "EBIOSRM_RAPPORT_PTR", 120, 38);

    // Main title
    const maxTW = this.pageWidth - 2 * this.margin;
    this.pdf.setFontSize(18);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setTextColor(...COLORS.primary);
    this.pdf.text("Rapport d'Analyse de Risques:", 105, 65, { align: "center" });

    // Analysis name (dynamic — wrap if long)
    const mainTitle = contextInfo.analysisName || "Rapport d'Analyse de Risques";
    this.pdf.setFontSize(18);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setTextColor(...COLORS.primary);
    const ml = this.pdf.splitTextToSize(mainTitle, maxTW);
    ml.forEach((line, i) => this.pdf.text(line, 105, 80 + i * 7, { align: 'center' }));
    const extraY = (ml.length - 1) * 7;

    // EBIOS RM title
    this.pdf.setFontSize(24);
    this.pdf.setTextColor(...COLORS.primary);
    this.pdf.text("EBIOS RM", 105, 110 + extraY, { align: "center" });
    this.pdf.setTextColor(0, 0, 0);

    // Information table
    const tableStartY = 135 + extraY;
    const rowHeight = 8;

    // Table headers and data
    const tableData = [
      ["Programme", "Rapport Complet EBIOS RM"],
      [
        "Organisation",
        contextInfo.organizationName ||
          metadata.companyName ||
          "Non sp\u00e9cifi\u00e9",
      ],
      ["Classification", "Interne"],
      [
        "Fr\u00e9quence de r\u00e9vision",
        "Annuelle / chaque changement majeur",
      ],
      ["Statut", metadata.status || "Finalis\u00e9"],
      ["Date de g\u00e9n\u00e9ration", new Date().toLocaleDateString("fr-FR")],
    ];

    tableData.forEach((row, index) => {
      const y = tableStartY + index * rowHeight;

      // Label column (blue background)
      this.pdf.setFillColor(41, 82, 163);
      this.pdf.rect(15, y, 65, rowHeight, "FD");
      this.pdf.setTextColor(255, 255, 255);
      this.pdf.setFontSize(9);
      this.pdf.setFont("helvetica", "bold");
      this.pdf.text(row[0], 17, y + 5.5);

      // Value column (light gray background)
      this.pdf.setFillColor(235, 235, 235);
      this.pdf.rect(80, y, 115, rowHeight, "FD");
      this.pdf.setTextColor(0, 0, 0);
      this.pdf.setFont("helvetica", "normal");

      // Handle long text with wrapping
      const maxWidth = 110;
      const lines = this.pdf.splitTextToSize(row[1], maxWidth);
      if (lines.length > 1) {
        // If text is too long, truncate and add ellipsis
        const truncated =
          row[1].length > 50 ? row[1].substring(0, 47) + "..." : row[1];
        this.pdf.text(truncated, 82, y + 5.5);
      } else {
        this.pdf.text(row[1], 82, y + 5.5);
      }
    });

    // Version history table with optimized column widths
    const historyY = 200 + extraY;
    const headerHeight = 10; // Increased header height
    const dataRowHeight = 15; // Increased data row height for better text fit

    // Table headers with optimized widths (total = 180mm)
    const headers = [
      "Version",
      "Date",
      "Description, remarques",
      "Auteur",
      "V\u00e9rificateur",
      "Approbateur",
    ];
    const colWidths = [18, 22, 70, 30, 20, 20]; // Optimized widths - more space for description and author
    let xPos = 15;

    // Draw header row
    headers.forEach((header, i) => {
      // Set blue background for header
      this.pdf.setFillColor(41, 82, 163);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.5);
      this.pdf.rect(xPos, historyY, colWidths[i], headerHeight, "FD");

      // White text for headers with proper wrapping
      this.pdf.setTextColor(255, 255, 255);
      this.pdf.setFontSize(7); // Slightly smaller font for better fit
      this.pdf.setFont("helvetica", "bold");

      // Handle header text wrapping
      const maxHeaderWidth = colWidths[i] - 3;
      const headerLines = this.pdf.splitTextToSize(header, maxHeaderWidth);

      // Center text vertically in header
      const startY =
        historyY + headerHeight / 2 - (headerLines.length - 1) * 1.5;
      headerLines.forEach((line, lineIndex) => {
        this.pdf.text(line, xPos + 1.5, startY + lineIndex * 3);
      });

      xPos += colWidths[i];
    });

    // Add one sample row with current version info
    xPos = 15;
    const versionData = [
      metadata.version || "1.0",
      new Date().toLocaleDateString("fr-FR"),
      "Version initiale du rapport",
      metadata.createdBy || "Syst\u00e8me EBIOS RM",
      "",
      "",
    ];

    versionData.forEach((data, i) => {
      // Light gray background for data row
      this.pdf.setFillColor(245, 245, 245);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.5);
      this.pdf.rect(
        xPos,
        historyY + headerHeight,
        colWidths[i],
        dataRowHeight,
        "FD",
      );

      // Black text for data
      this.pdf.setTextColor(0, 0, 0);
      this.pdf.setFontSize(7);
      this.pdf.setFont("helvetica", "normal");

      if (data) {
        // Handle text wrapping with proper margins
        const maxColWidth = colWidths[i] - 3; // 1.5mm margin on each side
        const lines = this.pdf.splitTextToSize(data, maxColWidth);

        // Limit to maximum lines that fit in the row height
        const maxLines = Math.floor((dataRowHeight - 2) / 3); // 3mm line height, 2mm total margin
        const displayLines = lines.slice(0, maxLines);

        // If text was truncated, add ellipsis to last line
        if (lines.length > maxLines && displayLines.length > 0) {
          const lastLine = displayLines[displayLines.length - 1];
          const ellipsisWidth = this.pdf.getTextWidth("...");
          const availableWidth = maxColWidth - ellipsisWidth;

          // Truncate last line to fit ellipsis
          let truncatedLine = lastLine;
          while (
            this.pdf.getTextWidth(truncatedLine) > availableWidth &&
            truncatedLine.length > 0
          ) {
            truncatedLine = truncatedLine.slice(0, -1);
          }
          displayLines[displayLines.length - 1] = truncatedLine + "...";
        }

        // Center text vertically in the row
        const startY =
          historyY +
          headerHeight +
          dataRowHeight / 2 -
          (displayLines.length - 1) * 1.5;
        displayLines.forEach((line, lineIndex) => {
          this.pdf.text(line, xPos + 1.5, startY + lineIndex * 3);
        });
      }
      xPos += colWidths[i];
    });

    // Footer
    const footerY = this.pageHeight - 20;
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(
      "M\u00e9thodologie EBIOS RM - Analyse Compl\u00e8te de Risques Cybers\u00e9curit\u00e9",
      105,
      footerY,
      { align: "center" },
    );
    this.pdf.text(
      "Document confidentiel - Usage interne uniquement",
      105,
      footerY + 8,
      { align: "center" },
    );

    // Add watermark to default cover page FIRST (behind all content)
    console.log("Adding watermark to default cover page");
    this.addWatermark();

    // Cover page complete - caller should call generateTableOfContents() next
  }

  /**
   * Add professional page header
   */
  addPageHeader() {
    const currentPage = this.pdf.internal.getCurrentPageInfo().pageNumber;
    console.log(
      `Adding page header to page ${currentPage} (instance pageNumber: ${this.pageNumber})`,
    );

    // Add watermark to ALL pages (including page 1)
    console.log(`Adding watermark to page ${currentPage}`);
    this.addWatermark();

    // Skip actual header content on first page (cover page)
    if (this.pageNumber === 1) return;

    const headerY = 15;

    // Header line
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(
      this.margin,
      headerY + 5,
      this.pageWidth - this.margin,
      headerY + 5,
    );

    // Document title
    this.pdf.setFontSize(FONTS.small);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(this.metadata.title, this.margin, headerY);

    // Current section
    if (this.currentSection) {
      this.pdf.text(
        this.currentSection,
        this.pageWidth - this.margin,
        headerY,
        { align: "right" },
      );
    }
  }

  /**
   * Add professional page footer
   */
  addPageFooter() {
    const footerY = this.pageHeight - 10;
    const isEnglish = this.options.language === "en";

    // Footer line
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(
      this.margin,
      footerY - 5,
      this.pageWidth - this.margin,
      footerY - 5,
    );

    // Page number
    const currentPageNumber = this.pdf.internal.getCurrentPageInfo().pageNumber;
    this.pdf.setFontSize(FONTS.small);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(
      `${isEnglish ? "Page" : "Page"} ${currentPageNumber}`,
      this.pageWidth - this.margin,
      footerY,
      { align: "right" },
    );

    // Document info
    this.pdf.text(
      isEnglish ? "EBIOS RM Report" : "Rapport EBIOS RM",
      this.margin,
      footerY,
    );

    // Center info
    const centerText = isEnglish
      ? "Confidential - Internal Use Only"
      : "Confidentiel - Usage interne";
    this.pdf.text(centerText, this.pageWidth / 2, footerY, { align: "center" });
  }

  /**
   * Add semi-transparent diagonal watermark background
   * @param {string} text - Watermark text
   */
  addWatermark(text) {
    // If no text provided, use default based on language
    if (!text) {
      const isEnglish = this.options.language === "en";
      text = isEnglish
        ? "ebiosens - confidential report"
        : "ebiosens - rapport confidentiel";
    }

    const currentPage = this.pdf.internal.getCurrentPageInfo().pageNumber;
    console.log(`Adding diagonal watermark to page ${currentPage} : ${text}`);

    // Save current state
    this.pdf.saveGraphicsState();

    // Set opacity for watermark
    this.pdf.setGState(new this.pdf.GState({ opacity: 0.08 })); // Reduced opacity for subtlety

    // Position text for diagonal effect across the full page
    const centerX = this.pageWidth / 2;
    const centerY = this.pageHeight / 2;

    // Create diagonal watermarks with rotation
    const watermarkInstances = [
      {
        x: centerX - 40,
        y: centerY - 60,
        size: 32, // Much smaller font size
        angle: -45, // Diagonal angle in degrees
      },
      {
        x: centerX + 40,
        y: centerY + 60,
        size: 32, // Much smaller font size
        angle: -45, // Same diagonal angle
      },
    ];

    watermarkInstances.forEach((instance) => {
      // Save state for each instance
      this.pdf.saveGraphicsState();

      // Set font properties
      this.pdf.setFontSize(instance.size);
      this.pdf.setTextColor(140, 140, 140); // Lighter gray
      this.pdf.setFont("helvetica", "normal"); // Normal weight for subtlety

      // Apply rotation transformation
      // Translate to rotation point, rotate, then translate back
      const radians = (instance.angle * Math.PI) / 180;

      // Move to the text position
      this.pdf.text(text, instance.x, instance.y, {
        align: "center",
        angle: instance.angle, // jsPDF supports angle parameter
      });

      // Restore state for this instance
      this.pdf.restoreGraphicsState();
    });

    console.log(
      `Diagonal watermark added successfully to page ${currentPage} with rotation`,
    );

    // Restore main graphics state
    this.pdf.restoreGraphicsState();
  }

  /**
   * Add section heading with TOC entry
   * @param {string} text - Heading text
   * @param {number} level - Heading level (1-3)
   * @param {object} options - Optional settings
   * @param {boolean} options.skipNumbering - If true, skip auto-numbering for level 1 headings
   */
  addSectionHeading(text, level = 1, options = {}) {
    // Clean text first
    const cleanedText = this.cleanText(text);
    if (!cleanedText) return;

    // Prepare display text with section numbering for level 1
    let displayText = cleanedText;
    let tocTitle = cleanedText;

    if (level === 1 && !cleanedText.match(/^\d+\./) && !options.skipNumbering) {
      // Count existing level 1 sections (excluding TOC which starts with "0.")
      const sectionNumber =
        this.tableOfContents.filter(
          (entry) => entry.level === 1 && !entry.title.startsWith("0."),
        ).length + 1;
      displayText = `${sectionNumber}. ${cleanedText}`;
      tocTitle = displayText; // Use numbered version in TOC
    }

    // Set current section for headers (use shorter version for headers)
    if (level === 1) {
      this.currentSection =
        tocTitle.length > 50 ? tocTitle.substring(0, 47) + "..." : tocTitle;
    }

    const fontSize =
      level === 1
        ? FONTS.heading1
        : level === 2
          ? FONTS.heading2
          : FONTS.heading3;

    // Use vibrant colored text instead of background bars
    const color =
      level === 1
        ? COLORS.primary
        : level === 2
          ? COLORS.indigo
          : level === 3
            ? COLORS.teal
            : COLORS.text;

    const spacing = level === 1 ? 12 : level === 2 ? 10 : 8;

    // Check page break BEFORE adding the heading content
    this.checkPageBreak(20);

    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(...color); // Always colored text
    this.pdf.setFont("helvetica", "bold");

    // Handle text wrapping for long headings
    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.pdf.splitTextToSize(displayText, maxWidth);

    lines.forEach((line, index) => {
      // Check if we need a page break for this line
      this.checkPageBreak(
        this.lineHeight + (index === lines.length - 1 ? spacing : 0),
      );

      // Position each line below the previous one
      const lineY = this.currentY + index * this.lineHeight;
      this.pdf.text(line, this.margin, lineY, { baseline: "middle" });
    });

    // Advance by the total height used plus spacing
    this.currentY += lines.length * this.lineHeight + spacing;

    // Add to table of contents AFTER the heading has been placed (with correct page number)
    // Exclude TOC title itself to prevent duplication
    if (level <= 2 && !cleanedText.includes("Table des Mati\u00e8res")) {
      this.tableOfContents.push({
        title:
          tocTitle.length > 80 ? tocTitle.substring(0, 77) + "..." : tocTitle,
        page: this.pageNumber,
        level: level,
      });
    }
  }

  /**
   * Add professional section introduction
   * @param {string} text - Introduction text
   */
  addSectionIntro(text) {
    // Clean the text first
    const cleanedText = this.cleanText(text);
    if (!cleanedText) return;

    // Check if we need a page break
    this.checkPageBreak(this.lineHeight * 2 + 5);

    // Use slightly smaller font and italic style for introductions
    this.pdf.setFontSize(FONTS.small);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "italic");

    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.pdf.splitTextToSize(cleanedText, maxWidth);

    lines.forEach((line) => {
      this.pdf.text(line, this.margin, this.currentY);
      this.currentY += this.lineHeight;
    });

    // Add extra spacing after introduction
    this.currentY += LAYOUT.paragraphSpacing + 2;
  }

  /**
   * Add professional paragraph with enhanced formatting
   * @param {string} text - Paragraph text
   * @param {Object} options - Formatting options
   */
  addParagraph(text, options = {}) {
    const {
      fontSize = FONTS.body,
      color = COLORS.text,
      fontStyle = "normal",
      indent = 0,
      lineSpacing = 1.3,
      alignment = "left",
      maxWidth = null,
    } = options;

    // Clean the text first
    const cleanedText = this.cleanText(text);
    if (!cleanedText) return;

    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(...color);
    this.pdf.setFont("helvetica", fontStyle);

    const effectiveMaxWidth =
      maxWidth || this.pageWidth - 2 * this.margin - indent;
    const lines = this.pdf.splitTextToSize(cleanedText, effectiveMaxWidth);
    const adjustedLineHeight = this.lineHeight * lineSpacing;

    lines.forEach((line) => {
      this.checkPageBreak(adjustedLineHeight + 2);

      const x =
        alignment === "center"
          ? this.pageWidth / 2
          : alignment === "right"
            ? this.pageWidth - this.margin
            : this.margin + indent;

      const alignOptions =
        alignment === "center"
          ? { align: "center" }
          : alignment === "right"
            ? { align: "right" }
            : {};

      this.pdf.text(line, x, this.currentY, alignOptions);
      this.currentY += adjustedLineHeight;
    });

    this.currentY += LAYOUT.paragraphSpacing;
  }

  /**
   * Add professional bullet list
   * @param {Array} items - List items
   * @param {Object} options - Formatting options
   */
  addBulletList(items, options = {}) {
    const {
      bulletStyle = "•",
      indent = 5,
      itemSpacing = 4,
      fontSize = FONTS.body,
    } = options;

    items.forEach((item) => {
      this.checkPageBreak(this.lineHeight + 4);

      this.pdf.setFontSize(fontSize);
      this.pdf.setTextColor(...COLORS.text);
      this.pdf.setFont("helvetica", "normal");

      // Add bullet
      this.pdf.text(bulletStyle, this.margin + indent, this.currentY);

      // Add text with proper wrapping
      const maxWidth = this.pageWidth - 2 * this.margin - indent - 8;
      const lines = this.pdf.splitTextToSize(item, maxWidth);

      lines.forEach((line, index) => {
        if (index > 0) {
          this.checkPageBreak(this.lineHeight + 2);
          this.currentY += this.lineHeight;
        }
        this.pdf.text(line, this.margin + indent + 8, this.currentY);
      });

      this.currentY += this.lineHeight + itemSpacing;
    });
  }

  /**
   * Add professional table with enhanced styling
   * @param {Array} headers - Table headers
   * @param {Array} rows - Table rows
   * @param {Object} options - Table options
   */
  addTable(headers, rows, options = {}) {
    const {
      title = null,
      headerStyle = "bold",
      alternateRows = true,
      borderStyle = "grid",
      fontSize = FONTS.small,
      columnWidths = null,
    } = options;

    this.checkPageBreak(40);

    if (title) {
      this.addSectionHeading(title, 3);
    }

    // Use autoTable plugin for professional tables
    const tableOptions = {
      startY: this.currentY,
      head: [headers],
      body: rows,
      theme: "grid",
      styles: {
        fontSize: fontSize,
        cellPadding: 3,
        textColor: COLORS.text,
        lineColor: COLORS.border,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: COLORS.primary,
        textColor: [255, 255, 255],
        fontStyle: headerStyle,
        fontSize: fontSize + 1,
      },
      alternateRowStyles: alternateRows
        ? {
            fillColor: COLORS.lighter,
          }
        : {},
      columnStyles: columnWidths ? this.generateColumnStyles(columnWidths) : {},
      margin: { left: this.margin, right: this.margin },
      didDrawPage: (data) => {
        // Update current Y position after table
        this.currentY = data.cursor.y + 5;
      },
    };

    // Track pages before table generation
    const pagesBefore = this.pdf.internal.getNumberOfPages();

    this.pdf.autoTable(tableOptions);

    // After autoTable, check if new pages were created and add watermarks
    const pagesAfter = this.pdf.internal.getNumberOfPages();
    if (pagesAfter > pagesBefore) {
      console.log(
        `${pagesAfter - pagesBefore} new pages created by table, adding watermarks...`,
      );
      const currentPageBeforeLoop =
        this.pdf.internal.getCurrentPageInfo().pageNumber;

      for (let i = pagesBefore + 1; i <= pagesAfter; i++) {
        // Switch to each new page and add watermark
        this.pdf.setPage(i);
        console.log(`Adding watermark to table page ${i}`);
        this.addWatermark();
      }

      // Return to the last table page
      this.pdf.setPage(pagesAfter);
      console.log(`Returned to page ${pagesAfter} after adding watermarks`);
    }
  }

  /**
   * Generate column styles for autoTable
   * @param {Array} widths - Column widths
   * @returns {Object} Column styles
   */
  generateColumnStyles(widths) {
    const styles = {};
    widths.forEach((width, index) => {
      styles[index] = { cellWidth: width };
    });
    return styles;
  }

  /**
   * Check if page break is needed and add new page
   * @param {number} requiredSpace - Required space in mm
   */
  checkPageBreak(requiredSpace = 20) {
    if (
      this.currentY + requiredSpace >
      this.pageHeight - LAYOUT.footerHeight - this.margin
    ) {
      const pageBeforeBreak = this.pdf.internal.getCurrentPageInfo().pageNumber;
      console.log(
        `Creating page break at page ${pageBeforeBreak}, will call addPageHeader()`,
      );

      // Add footer to current page
      this.addPageFooter();

      // Create new page
      this.pdf.addPage();

      // Update page number and position
      this.pageNumber++;
      this.currentY = this.margin + LAYOUT.headerHeight;

      console.log(`Page break created. New page: ${this.pageNumber}`);

      // Add header and watermark to ALL new pages
      console.log(`Calling addPageHeader() for page ${this.pageNumber}`);
      this.addPageHeader();
    }
  }

  /**
   * Generate table of contents (call this after all content is added)
   */
  generateTableOfContents() {
    // Define localized strings
    const isEnglish = this.options.language === "en";
    const tocTitle = isEnglish
      ? "Table of Contents"
      : "Table des Mati\u00e8res";
    const tocContinued = isEnglish
      ? "Table of Contents (continued)"
      : "Table des Mati\u00e8res (suite)";

    if (!this.tocPageNumber) {
      console.warn("No TOC page reserved, creating new page");
      this.pdf.addPage();
      this.tocPageNumber = this.pdf.internal.getNumberOfPages();
      this.tocPagesReserved = 1;
    }

    // Store current page to return to it later
    const currentPage = this.pdf.internal.getCurrentPageInfo().pageNumber;

    // Track TOC generation state
    let tocCurrentPage = 0;
    let tocPagesUsed = 0;

    // Start with the first reserved TOC page
    this.pdf.setPage(this.tocPageNumber);
    tocCurrentPage = this.tocPageNumber;
    tocPagesUsed = 1;

    // Clear the page content (simulate clearing by drawing white rectangle)
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.rect(0, 0, this.pageWidth, this.pageHeight, "F");

    // Re-add watermark after clearing the page
    console.log(`Adding watermark to TOC page ${tocCurrentPage}`);
    this.addWatermark();

    // Initialize TOC content
    this.currentY = this.margin + LAYOUT.headerHeight;
    this.addPageHeader();

    // Add TOC title manually (don't use addSectionHeading as it would add TOC to itself)
    this.checkPageBreak(20);

    this.pdf.setFontSize(FONTS.heading1);
    this.pdf.setTextColor(...COLORS.primary);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text(tocTitle, this.margin, this.currentY, { baseline: "middle" });
    this.currentY += this.lineHeight + 2;
    this.addSpace(2);

    // Add TOC entries with proper page break handling
    this.tableOfContents.forEach((entry, index) => {
      // Check if we need a page break for TOC
      const needsPageBreak = this.checkTOCPageBreakNeeded();

      if (needsPageBreak) {
        // Add footer to current page
        this.addPageFooter();

        // Check if we have more reserved pages
        if (tocPagesUsed < this.tocPagesReserved) {
          // Use next reserved page
          tocCurrentPage++;
          tocPagesUsed++;
          this.pdf.setPage(tocCurrentPage);
        } else {
          // Need to add a new page beyond reserved ones
          this.pdf.addPage();
          tocCurrentPage = this.pdf.internal.getNumberOfPages();
          tocPagesUsed++;

          // Update page numbers for all content after TOC
          this.adjustPageNumbersAfterTOC();
        }

        // Clear new page and set up
        this.pdf.setFillColor(255, 255, 255);
        this.pdf.rect(0, 0, this.pageWidth, this.pageHeight, "F");

        // Add watermark to new TOC page
        console.log(`Adding watermark to new TOC page ${tocCurrentPage}`);
        this.addWatermark();

        this.currentY = this.margin + LAYOUT.headerHeight;
        this.addPageHeader();

        // Add continuation header
        this.pdf.setFontSize(FONTS.heading3);
        this.pdf.setTextColor(...COLORS.muted);
        this.pdf.setFont("helvetica", "italic");
        this.pdf.text(tocContinued, this.margin, this.currentY);
        this.currentY += 10;
      }

      // Calculate available width for title (reserve space for page number and dots)
      const pageNumWidth = this.pdf.getTextWidth(entry.page.toString());
      const availableWidth =
        this.pageWidth - this.margin * 2 - pageNumWidth - 20; // 20mm for dots and spacing

      this.pdf.setFontSize(FONTS.body);
      this.pdf.setTextColor(...COLORS.text);
      this.pdf.setFont("helvetica", "normal");

      // Handle long titles with text wrapping
      const titleLines = this.pdf.splitTextToSize(entry.title, availableWidth);

      titleLines.forEach((line, lineIndex) => {
        if (lineIndex > 0) {
          // Check page break for continuation lines
          if (this.checkTOCPageBreakNeeded()) {
            // Handle page break for wrapped text
            this.addPageFooter();

            if (tocPagesUsed < this.tocPagesReserved) {
              tocCurrentPage++;
              tocPagesUsed++;
              this.pdf.setPage(tocCurrentPage);
            } else {
              this.pdf.addPage();
              tocCurrentPage = this.pdf.internal.getNumberOfPages();
              tocPagesUsed++;
              this.adjustPageNumbersAfterTOC();
            }

            this.pdf.setFillColor(255, 255, 255);
            this.pdf.rect(0, 0, this.pageWidth, this.pageHeight, "F");

            // Add watermark to continuation TOC page
            console.log(
              `Adding watermark to TOC continuation page ${tocCurrentPage}`,
            );
            this.addWatermark();

            this.currentY = this.margin + LAYOUT.headerHeight;
            this.addPageHeader();

            this.pdf.setFontSize(FONTS.heading3);
            this.pdf.setTextColor(...COLORS.muted);
            this.pdf.setFont("helvetica", "italic");
            this.pdf.text(tocContinued, this.margin, this.currentY);
            this.currentY += 10;

            // Reset font for content
            this.pdf.setFontSize(FONTS.body);
            this.pdf.setTextColor(...COLORS.text);
            this.pdf.setFont("helvetica", "normal");
          } else {
            this.currentY += 5;
          }
        }

        // Title (or part of title)
        this.pdf.setTextColor(...COLORS.text);
        this.pdf.text(
          line,
          this.margin + (entry.level > 1 ? 10 : 0),
          this.currentY,
        );

        // Only add dots and page number on the last line of the title
        if (lineIndex === titleLines.length - 1) {
          const titleWidth = this.pdf.getTextWidth(line);
          const startDotsX =
            this.margin + (entry.level > 1 ? 10 : 0) + titleWidth + 2;
          const endDotsX = this.pageWidth - this.margin - pageNumWidth - 2;
          const dotsWidth = endDotsX - startDotsX;

          if (dotsWidth > 0) {
            const dotCount = Math.floor(dotsWidth / this.pdf.getTextWidth("."));
            const dots = ".".repeat(Math.max(0, dotCount));

            // Dots
            this.pdf.setTextColor(...COLORS.muted);
            this.pdf.text(dots, startDotsX, this.currentY);
          }

          // Page number (adjust for additional TOC pages if needed)
          const adjustedPageNumber =
            entry.page + Math.max(0, tocPagesUsed - this.tocPagesReserved);
          this.pdf.setTextColor(...COLORS.text);
          this.pdf.text(
            adjustedPageNumber.toString(),
            this.pageWidth - this.margin,
            this.currentY,
            { align: "right" },
          );
        }
      });

      this.currentY += 9; // Balanced spacing between TOC lines for good readability
    });

    // Add footer to final TOC page
    this.addPageFooter();

    // Store actual TOC pages used for future reference
    this.tocPagesActual = tocPagesUsed;

    // Return to the current page
    this.pdf.setPage(currentPage);
  }

  /**
   * Check if page break is needed for table of contents (without actually breaking)
   * @returns {boolean} True if page break is needed
   */
  checkTOCPageBreakNeeded() {
    const requiredSpace = 15; // Space needed for TOC entry
    return (
      this.currentY + requiredSpace >
      this.pageHeight - LAYOUT.footerHeight - this.margin
    );
  }

  /**
   * Adjust page numbers in TOC entries when additional TOC pages are added
   */
  adjustPageNumbersAfterTOC() {
    // This would be called if we add pages beyond the reserved ones
    // In practice, the page number adjustment is handled in the display logic above
    console.log(
      "Additional TOC page added - page numbers will be adjusted in display",
    );
  }

  /**
   * Generate document structure with proper page order
   * @param {Object} reportData - Report data
   */
  generateDocumentStructure(reportData) {
    // 1. Cover page (page 1)
    this.generateCoverPage(reportData);

    // 2. Reserve space for table of contents - will be filled later
    this.reserveTableOfContentsPages();

    // 3. Content starts on next page with executive summary as chapter 1
    this.startContentSection();
    this.generateExecutiveSummary(reportData);
  }

  /**
   * Reserve pages for table of contents (to be filled later)
   * Estimates the number of pages needed based on expected content
   */
  reserveTableOfContentsPages() {
    // Estimate TOC pages needed (realistic estimate for EBIOS RM reports)
    const estimatedTOCEntries = 50; // Realistic number for typical EBIOS RM reports
    const entriesPerPage = 35; // Conservative estimate of entries per page
    const estimatedTOCPages = Math.max(
      1,
      Math.ceil(estimatedTOCEntries / entriesPerPage),
    );

    // Store the starting page number for TOC
    this.tocPageNumber = this.pageNumber + 1;
    this.tocPagesReserved = estimatedTOCPages;

    // Reserve the estimated number of pages (blank pages with headers/footers and watermarks)
    for (let i = 0; i < estimatedTOCPages; i++) {
      this.pdf.addPage();
      this.pageNumber++;

      // Set up page with headers, footers and watermarks
      this.currentY = this.margin + LAYOUT.headerHeight;
      this.addPageHeader(); // This now includes watermark
      this.addPageFooter();
    }
  }

  /**
   * Start content section after table of contents
   */
  startContentSection() {
    this.pdf.addPage();
    this.pageNumber++;
    this.currentY = this.margin + LAYOUT.headerHeight;
    this.addPageHeader(); // This now includes watermark
  }

  /**
   * Add space
   * @param {number} space - Space in mm
   */
  addSpace(space = 5) {
    this.currentY += space;
  }

  /**
   * Add image from data URL
   * @param {string} dataUrl - Base64 data URL of the image
   * @param {Object} options - Image options
   */
  async addImageFromDataUrl(dataUrl, options = {}) {
    const {
      width = 100,
      height = 75,
      align = "left",
      caption = null,
    } = options;

    // Check if we need a page break
    this.checkPageBreak(height + 20);

    // Calculate position based on alignment
    let x = this.margin;
    if (align === "center") {
      x = (this.pageWidth - width) / 2;
    } else if (align === "right") {
      x = this.pageWidth - this.margin - width;
    }

    try {
      // Validate data URL
      if (!dataUrl || typeof dataUrl !== "string") {
        throw new Error("Invalid data URL provided");
      }

      if (!dataUrl.startsWith("data:image/")) {
        throw new Error("Data URL is not a valid image format");
      }

      // Detect image format from data URL
      let format = "PNG";
      if (
        dataUrl.includes("data:image/jpeg") ||
        dataUrl.includes("data:image/jpg")
      ) {
        format = "JPEG";
      } else if (dataUrl.includes("data:image/gif")) {
        format = "GIF";
      }

      console.log("📸 Adding image to PDF:", {
        format,
        width,
        height,
        x,
        y: this.currentY,
        dataUrlLength: dataUrl.length,
        dataUrlPrefix: dataUrl.substring(0, 50),
      });

      // Add the image to the PDF
      this.pdf.addImage(dataUrl, format, x, this.currentY, width, height);
      this.currentY += height + 5;

      console.log("✅ Image added successfully to PDF");

      // Add caption if provided
      if (caption) {
        this.pdf.setFontSize(FONTS.small);
        this.pdf.setTextColor(...COLORS.muted);
        this.pdf.setFont("helvetica", "italic");

        const captionLines = this.pdf.splitTextToSize(caption, width);
        captionLines.forEach((line) => {
          this.pdf.text(
            line,
            align === "center" ? x + width / 2 : x,
            this.currentY,
            {
              align: align === "center" ? "center" : "left",
            },
          );
          this.currentY += this.lineHeight;
        });

        // Reset font
        this.pdf.setFont("helvetica", "normal");
        this.pdf.setTextColor(...COLORS.text);
      }

      this.addSpace(5);
    } catch (error) {
      console.error("❌ Error adding image to PDF:", error);
      console.error("❌ Error details:", {
        message: error.message,
        stack: error.stack,
        dataUrlLength: dataUrl?.length || 0,
        dataUrlPrefix: dataUrl?.substring(0, 100) || "N/A",
      });

      // Add error message instead of image
      this.pdf.setFontSize(FONTS.small);
      this.pdf.setTextColor(...COLORS.danger);
      this.pdf.text(
        `Erreur lors du chargement de l'image: ${error.message}`,
        x,
        this.currentY,
      );
      this.currentY += 10;
      this.pdf.setTextColor(...COLORS.text);

      // Re-throw the error so calling code can handle it
      throw error;
    }
  }

  /**
   * Format date for display
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date
   */
  formatDate(date) {
    if (!date) return "Non sp\u00e9cifi\u00e9";

    const dateObj = new Date(date);
    return dateObj.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  /**
   * Ensure all pages have watermarks (call before finalize if needed)
   */
  ensureAllPagesHaveWatermarks() {
    const totalPages = this.pdf.internal.getNumberOfPages();
    const currentPage = this.pdf.internal.getCurrentPageInfo().pageNumber;

    console.log(`Ensuring watermarks on all ${totalPages} pages`);

    for (let i = 1; i <= totalPages; i++) {
      this.pdf.setPage(i);
      // Add watermark to each page (it's safe to call multiple times)
      console.log(`Adding/refreshing watermark on page ${i}`);
      this.addWatermark();
    }

    // Return to original page
    this.pdf.setPage(currentPage);
    console.log(`Returned to page ${currentPage} after watermark refresh`);
  }

  /**
   * Finalize document and prepare for output
   */
  finalize() {
    // Ensure all pages have watermarks before finalizing
    this.ensureAllPagesHaveWatermarks();

    // Update total pages in metadata
    this.totalPages = this.pdf.internal.getNumberOfPages();

    // Set final metadata
    this.pdf.setProperties({
      ...this.metadata,
      subject: `${this.metadata.subject} - ${this.totalPages} pages`,
    });
  }

  /**
   * Save PDF with filename
   * @param {string} filename - Output filename
   */
  save(filename = "rapport-ebios-rm.pdf") {
    this.finalize();
    this.pdf.save(filename);
  }

  /**
   * Get PDF as blob
   * @returns {Blob} PDF blob
   */
  getBlob() {
    this.finalize();
    return this.pdf.output("blob");
  }

  /**
   * Get PDF as data URL
   * @returns {string} PDF data URL
   */
  getDataURL() {
    this.finalize();
    return this.pdf.output("dataurlstring");
  }

  /**
   * Add professional info box
   * @param {string} title - Box title
   * @param {string} content - Box content
   * @param {Object} options - Box options
   */
  addInfoBox(title, content, options = {}) {
    const {
      type = "info",
      width = this.pageWidth - 2 * this.margin,
      icon = null,
    } = options;

    const typeColors = {
      info: { bg: [239, 246, 255], border: COLORS.accent, icon: "ℹ" },
      success: { bg: [240, 253, 244], border: COLORS.success, icon: "✓" },
      warning: { bg: [255, 251, 235], border: COLORS.warning, icon: "⚠" },
      danger: { bg: [254, 242, 242], border: COLORS.danger, icon: "✕" },
    };

    const colors = typeColors[type] || typeColors.info;
    const boxHeight = 30; // Minimum height, will expand based on content

    this.checkPageBreak(boxHeight + 10);

    // Box background
    this.pdf.setFillColor(...colors.bg);
    this.pdf.setDrawColor(...colors.border);
    this.pdf.setLineWidth(1);
    this.pdf.roundedRect(
      this.margin,
      this.currentY,
      width,
      boxHeight,
      3,
      3,
      "FD",
    );

    // Left accent bar
    this.pdf.setFillColor(...colors.border);
    this.pdf.rect(this.margin, this.currentY, 4, boxHeight, "F");

    // Icon
    const iconX = this.margin + 8;
    const iconY = this.currentY + 10;

    if (icon || colors.icon) {
      this.pdf.setFontSize(FONTS.heading3);
      this.pdf.setTextColor(...colors.border);
      this.pdf.setFont("helvetica", "bold");
      this.pdf.text(icon || colors.icon, iconX, iconY);
    }

    // Title
    const titleX = iconX + 10;
    this.pdf.setFontSize(FONTS.body);
    this.pdf.setTextColor(...colors.border);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text(title, titleX, iconY);

    // Content
    this.pdf.setFontSize(FONTS.small);
    this.pdf.setTextColor(...COLORS.text);
    this.pdf.setFont("helvetica", "normal");

    const maxWidth = width - 25;
    const lines = this.pdf.splitTextToSize(content, maxWidth);

    let contentY = iconY + 8;
    lines.forEach((line) => {
      this.pdf.text(line, titleX, contentY);
      contentY += 4;
    });

    this.currentY += Math.max(boxHeight, contentY - this.currentY + 5);
  }

  /**
   * Add status badge with professional styling
   * @param {string} text - Badge text
   * @param {string} status - Status type
   * @param {number} x - X position
   * @param {number} y - Y position
   * @returns {number} Badge width
   */
  addStatusBadge(text, status, x = null, y = null) {
    const statusColors = {
      success: { bg: COLORS.success, text: [255, 255, 255] },
      warning: { bg: COLORS.warning, text: [255, 255, 255] },
      danger: { bg: COLORS.danger, text: [255, 255, 255] },
      info: { bg: COLORS.accent, text: [255, 255, 255] },
      default: { bg: COLORS.muted, text: [255, 255, 255] },
    };

    const color = statusColors[status] || statusColors.default;
    const padding = 3;

    this.pdf.setFontSize(FONTS.tiny);
    const textWidth = this.pdf.getTextWidth(text);
    const badgeWidth = textWidth + padding * 2;
    const badgeHeight = 5;

    const badgeX = x || this.margin;
    const badgeY = y || this.currentY;

    // Badge background
    this.pdf.setFillColor(...color.bg);
    this.pdf.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 1, 1, "F");

    // Badge text
    this.pdf.setTextColor(...color.text);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text(text, badgeX + padding, badgeY + 3.5);

    if (!x && !y) {
      this.currentY += badgeHeight + 3;
    }

    return badgeWidth;
  }

  /**
   * Add professional separator line
   * @param {Object} options - Line options
   */
  addSeparatorLine(options = {}) {
    const {
      color = COLORS.border,
      width = 0.5,
      style = "solid",
      margin = 0,
    } = options;

    this.pdf.setDrawColor(...color);
    this.pdf.setLineWidth(width);

    if (style === "dashed") {
      this.pdf.setLineDashPattern([2, 2], 0);
    }

    this.pdf.line(
      this.margin + margin,
      this.currentY,
      this.pageWidth - this.margin - margin,
      this.currentY,
    );

    if (style === "dashed") {
      this.pdf.setLineDashPattern([], 0); // Reset to solid
    }

    this.currentY += 8;
  }

  /**
   * Add KPI card with professional styling
   * @param {string} title - KPI title
   * @param {string} value - KPI value
   * @param {Object} options - Styling options
   */
  addKPICard(title, value, options = {}) {
    const {
      color = COLORS.primary,
      width = 40,
      height = 25,
      unit = "",
      trend = null,
    } = options;

    this.checkPageBreak(height + 10);

    const cardX = this.margin;
    const cardY = this.currentY;

    // Card background
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.roundedRect(cardX, cardY, width, height, 2, 2, "FD");

    // Accent bar
    this.pdf.setFillColor(...color);
    this.pdf.rect(cardX, cardY, 3, height, "F");

    // Title
    this.pdf.setFontSize(FONTS.small);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(title, cardX + 6, cardY + 8);

    // Value
    this.pdf.setFontSize(FONTS.heading2);
    this.pdf.setTextColor(...color);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text(`${value}${unit}`, cardX + 6, cardY + 18);

    // Trend indicator
    if (trend) {
      const trendColor =
        trend > 0 ? COLORS.success : trend < 0 ? COLORS.danger : COLORS.muted;
      const trendSymbol = trend > 0 ? "↗" : trend < 0 ? "↘" : "→";

      this.pdf.setFontSize(FONTS.body);
      this.pdf.setTextColor(...trendColor);
      this.pdf.text(trendSymbol, cardX + width - 8, cardY + 18);
    }

    return width + 5; // Return width for positioning next card
  }

  /**
   * Add space (compatibility method)
   * @param {number} amount - Amount of space in mm
   */
  addSpace(amount) {
    this.currentY += amount;
    this.checkPageBreak(0);
  }

  /**
   * Add new page (compatibility method)
   * @param {string} orientation - 'portrait' or 'landscape' (default: portrait)
   */
  addNewPage(orientation = "portrait") {
    // Map orientation names to jsPDF format
    const orientationCode =
      orientation === "landscape" || orientation === "l" ? "l" : "p";
    this.pdf.addPage("a4", orientationCode);

    // Update page dimensions for landscape if needed
    if (orientationCode === "l") {
      this.pageWidth = 297; // A4 landscape width
      this.pageHeight = 210; // A4 landscape height
    } else {
      this.pageWidth = 210; // A4 portrait width
      this.pageHeight = 297; // A4 portrait height
    }

    this.currentY = this.margin + LAYOUT.headerHeight;
    this.pageNumber++;
    this.addPageHeader(); // This now includes watermark
  }

  /**
   * Add text at specific position (compatibility method)
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {string} text - Text to add
   * @param {Object} options - Formatting options
   */
  addTextAt(x, y, text, options = {}) {
    const {
      fontSize = FONTS.body,
      color = COLORS.text,
      fontWeight = "normal",
      align = "left",
      maxWidth = null,
    } = options;

    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(...color);
    this.pdf.setFont("helvetica", fontWeight);

    const effectiveMaxWidth = maxWidth || this.pageWidth - x - this.margin;
    const lines = this.pdf.splitTextToSize(text, effectiveMaxWidth);

    const alignOptions =
      align === "center"
        ? { align: "center" }
        : align === "right"
          ? { align: "right" }
          : {};

    lines.forEach((line, index) => {
      const currentY = y + index * this.lineHeight;
      this.pdf.text(line, x, currentY, alignOptions);
    });
  }

  /**
   * Add centered text (compatibility method)
   * @param {string} text - Text to center
   * @param {Object} options - Formatting options
   */
  addCenteredText(text, options = {}) {
    const {
      fontSize = FONTS.body,
      color = COLORS.text,
      fontWeight = "normal",
    } = options;

    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(...color);
    this.pdf.setFont("helvetica", fontWeight);

    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.pdf.splitTextToSize(text, maxWidth);

    lines.forEach((line) => {
      this.checkPageBreak(this.lineHeight + 2);
      this.pdf.text(line, this.pageWidth / 2, this.currentY, {
        align: "center",
      });
      this.currentY += this.lineHeight;
    });
  }

  /**
   * Add rounded rectangle (compatibility method)
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {number} width - Width
   * @param {number} height - Height
   * @param {number} radius - Corner radius
   * @param {Object} options - Style options
   */
  addRoundedRectangle(x, y, width, height, radius, options = {}) {
    const {
      fillColor = null,
      borderColor = COLORS.border,
      borderWidth = 1,
    } = options;

    if (fillColor) {
      if (typeof fillColor === "string") {
        const rgb = this.hexToRgb(fillColor);
        this.pdf.setFillColor(...rgb);
      } else {
        this.pdf.setFillColor(...fillColor);
      }
    }

    this.pdf.setDrawColor(...borderColor);
    this.pdf.setLineWidth(borderWidth);

    const style = fillColor ? "FD" : "D";
    this.pdf.roundedRect(x, y, width, height, radius, radius, style);
  }

  /**
   * Convert hex color to RGB array (compatibility method)
   * @param {string|Array} color - Color value
   * @returns {Array} RGB array
   */
  hexToRgb(color) {
    if (Array.isArray(color)) {
      return color;
    }

    if (typeof color === "string") {
      const hex = color.replace("#", "");
      if (hex.length === 6) {
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return [r, g, b];
      }
    }

    // Default to black if invalid
    return [0, 0, 0];
  }

  /**
   * Add page header (compatibility method)
   * @param {string} title - Header title
   */
  addPageHeader(title = null) {
    if (this.pageNumber === 1) return; // Skip header on first page

    const headerY = 15;

    // Header line
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(
      this.margin,
      headerY + 5,
      this.pageWidth - this.margin,
      headerY + 5,
    );

    // Document title
    this.pdf.setFontSize(FONTS.small);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(title || this.metadata.title, this.margin, headerY);

    // Current section
    if (this.currentSection) {
      this.pdf.text(
        this.currentSection,
        this.pageWidth - this.margin,
        headerY,
        { align: "right" },
      );
    }
  }

  /**
   * Add divider line (compatibility method)
   * @param {Object} options - Line options
   */
  addDividerLine(options = {}) {
    const { color = COLORS.border, width = 0.5, margin = 0 } = options;

    this.pdf.setDrawColor(...color);
    this.pdf.setLineWidth(width);

    this.pdf.line(
      this.margin + margin,
      this.currentY,
      this.pageWidth - this.margin - margin,
      this.currentY,
    );

    this.currentY += 8;
  }

  /**
   * Add chapter header (compatibility method)
   * @param {string} title - Chapter title
   */
  addChapterHeader(title) {
    this.addSectionHeading(title, 1);
  }

  /**
   * Add metric cards (compatibility method)
   * @param {Array} metrics - Array of metric objects
   * @param {Object} options - Layout options
   */
  addMetricCards(metrics, options = {}) {
    const { columns = 2, spacing = 10 } = options;

    metrics.forEach((metric, index) => {
      if (index % columns === 0 && index > 0) {
        this.currentY += 30; // Move to next row
      }

      const x =
        this.margin +
        (index % columns) * ((this.pageWidth - 2 * this.margin) / columns);
      this.addKPICard(metric.label, metric.value, {
        color: metric.color || COLORS.primary,
        width: (this.pageWidth - 2 * this.margin) / columns - spacing,
      });
    });

    this.currentY += 35; // Space after cards
  }

  /**
   * Add two column layout (compatibility method)
   * @param {Object} columnData - Data for left and right columns
   * @param {Object} options - Layout options
   */
  addTwoColumnLayout(columnData, options = {}) {
    const { spacing = 15 } = options;
    const columnWidth = (this.pageWidth - 2 * this.margin - spacing) / 2;

    // Left column
    const leftX = this.margin;
    this.addSectionHeading(columnData.left.title, 3);
    columnData.left.content.forEach((item) => {
      this.addParagraph(`• ${item}`);
    });

    // Right column (would need more complex positioning)
    this.addSpace(10);
    this.addSectionHeading(columnData.right.title, 3);
    columnData.right.content.forEach((item) => {
      this.addParagraph(`• ${item}`);
    });
  }

  /**
   * Generate professional checklist PDF export
   * @param {Object} checklistData - Complete checklist data from database
   * @param {Object} options - Export options
   */
  generateChecklistPDF(checklistData, options = {}) {
    const {
      title = "Fiche de Suivi - Mise en \u0153uvre des Mesures",
      includeCoverPage = true,
      includeSummary = true,
    } = options;

    // Set document metadata
    this.metadata = {
      title: title,
      subject: "Checklist de suivi d'impl\u00e9mentation - EBIOS RM",
      author: "Syst\u00e8me EBIOS RM",
      creator: "EBIOS RM Checklist Export",
      keywords:
        "EBIOS RM, checklist, impl\u00e9mentation, cybers\u00e9curit\u00e9",
    };

    // Generate cover page if requested
    if (includeCoverPage) {
      this.generateChecklistCoverPage(checklistData);
    }

    // Generate summary section if requested
    if (includeSummary) {
      this.generateChecklistSummary(checklistData);
    }

    // Generate detailed checklist tables for each scenario
    this.generateDetailedChecklistTables(checklistData);
  }

  /**
   * Generate professional cover page for checklist PDF
   * @param {Object} checklistData - Checklist data
   */
  generateChecklistCoverPage(checklistData) {
    // Clean white background
    this.pdf.setFillColor(255, 255, 255);
    this.pdf.rect(0, 0, this.pageWidth, this.pageHeight, "F");

    const centerX = this.pageWidth / 2;
    let currentY = 60;

    // Simple header line
    this.pdf.setDrawColor(...COLORS.primary);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(this.margin, 40, this.pageWidth - this.margin, 40);

    // Main title with enhanced styling
    this.pdf.setFontSize(24);
    this.pdf.setTextColor(...COLORS.primary);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text("FICHE DE SUIVI", centerX, currentY, { align: "center" });

    currentY += 18;

    // Subtitle
    this.pdf.setFontSize(16);
    this.pdf.setTextColor(...COLORS.teal);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.text(
      "Mise en \u0153uvre des mesures de s\u00e9curit\u00e9",
      centerX,
      currentY,
      { align: "center" },
    );

    currentY += 40;

    // Checklist name
    if (checklistData.name) {
      this.pdf.setFontSize(18);
      this.pdf.setTextColor(...COLORS.dark);
      this.pdf.setFont("helvetica", "bold");
      this.pdf.text(checklistData.name, centerX, currentY, { align: "center" });
      currentY += 22;
    }

    // Description
    if (checklistData.description) {
      this.pdf.setFontSize(12);
      this.pdf.setTextColor(...COLORS.text);
      this.pdf.setFont("helvetica", "normal");
      const descLines = this.pdf.splitTextToSize(
        checklistData.description,
        140,
      );
      descLines.forEach((line) => {
        this.pdf.text(line, centerX, currentY, { align: "center" });
        currentY += 6;
      });
      currentY += 20;
    }

    // Information box
    const boxY = currentY + 10;
    const boxWidth = 140;
    const boxHeight = 50;
    const boxX = (this.pageWidth - boxWidth) / 2;

    // Clean box with subtle border
    this.pdf.setFillColor(250, 250, 250);
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.rect(boxX, boxY, boxWidth, boxHeight, "FD");

    // Box content
    let infoY = boxY + 12;
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(...COLORS.text);
    this.pdf.setFont("helvetica", "normal");

    const scenarioCount = checklistData.checklistResponses?.length || 0;
    const totalQuestions =
      checklistData.checklistResponses?.reduce((acc, scenario) => {
        const questions =
          scenario.checklistData?.reduce(
            (qAcc, measure) => qAcc + (measure.questions?.length || 0),
            0,
          ) || 0;
        const customTasks = scenario.customTasks?.length || 0;
        return acc + questions + customTasks;
      }, 0) || 0;

    const completedQuestions =
      checklistData.checklistResponses?.reduce((acc, scenario) => {
        const responses = Object.values(scenario.responses || {});
        return acc + responses.filter((r) => r === "fait").length;
      }, 0) || 0;

    const infoItems = [
      `Sc\u00e9narios analys\u00e9s: ${scenarioCount}`,
      `Questions totales: ${totalQuestions}`,
      `Questions valid\u00e9es: ${completedQuestions}`,
      `Date d'export: ${new Date().toLocaleDateString("fr-FR")}`,
    ];

    infoItems.forEach((item) => {
      this.pdf.text(item, boxX + 10, infoY, { align: "left" });
      infoY += 10;
    });

    // Simple footer
    const footerY = this.pageHeight - 40;

    // Footer line
    this.pdf.setDrawColor(...COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(
      this.margin,
      footerY - 10,
      this.pageWidth - this.margin,
      footerY - 10,
    );

    // Footer text
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...COLORS.muted);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.text(
      "M\u00e9thodologie EBIOS RM - Suivi d'impl\u00e9mentation",
      centerX,
      footerY,
      { align: "center" },
    );

    this.pdf.setFontSize(8);
    this.pdf.text(
      "Document confidentiel - Usage interne uniquement",
      centerX,
      footerY + 10,
      { align: "center" },
    );

    // Add watermark to checklist cover page
    console.log("Adding watermark to checklist cover page");
    this.addWatermark();

    // Start new page for content
    this.pdf.addPage();
    this.pageNumber++;
    this.currentY = this.margin + LAYOUT.headerHeight;
    this.addPageHeader();
  }

  /**
   * Generate checklist summary section
   * @param {Object} checklistData - Checklist data
   */
  generateChecklistSummary(checklistData) {
    this.addSectionHeading("1. R\u00c9SUM\u00c9 DES SC\u00c9NARIOS", 1);
    this.addSpace(10);

    if (
      !checklistData.checklistResponses ||
      checklistData.checklistResponses.length === 0
    ) {
      this.addParagraph("Aucun sc\u00e9nario trouv\u00e9 dans la checklist.");
      return;
    }

    // Summary table headers
    const headers = [
      "Sc\u00e9nario",
      "Progression",
      "Questions\nValid\u00e9es",
      "Questions\nTotales",
      "T\u00e2ches\nPersonnalis\u00e9es",
    ];

    // Prepare summary data
    const summaryRows = checklistData.checklistResponses.map((scenario) => {
      const scenarioName = scenario.scenarioName || "Sc\u00e9nario sans nom";
      const progress = scenario.progress || 0;
      const totalQuestions = scenario.totalQuestions || 0;
      const answeredQuestions = scenario.answeredQuestions || 0;
      const customTasksCount = scenario.customTasks?.length || 0;

      return [
        scenarioName,
        `${progress}%`,
        answeredQuestions.toString(),
        totalQuestions.toString(),
        customTasksCount.toString(),
      ];
    });

    // Add summary table
    this.addTable(headers, summaryRows, {
      title: "Aper\u00e7u des sc\u00e9narios",
      columnWidths: [60, 20, 20, 20, 25],
    });

    this.addSpace(15);
  }

  /**
   * Generate detailed checklist tables for each scenario
   * @param {Object} checklistData - Checklist data
   */
  generateDetailedChecklistTables(checklistData) {
    if (
      !checklistData.checklistResponses ||
      checklistData.checklistResponses.length === 0
    ) {
      return;
    }

    checklistData.checklistResponses.forEach((scenario, scenarioIndex) => {
      // Scenario section header
      const scenarioTitle = `${scenarioIndex + 2}. ${scenario.scenarioName || "Sc\u00e9nario sans nom"}`;
      this.addSectionHeading(scenarioTitle, 1);
      this.addSpace(5);

      // Scenario progress info
      const progress = scenario.progress || 0;
      const totalQuestions = scenario.totalQuestions || 0;
      const answeredQuestions = scenario.answeredQuestions || 0;
      const customTasksCount = scenario.customTasks?.length || 0;

      this.addParagraph(
        `Progression globale: ${progress}% (${answeredQuestions}/${totalQuestions} questions r\u00e9pondues)`,
      );
      if (customTasksCount > 0) {
        this.addParagraph(
          `T\u00e2ches personnalis\u00e9es: ${customTasksCount}`,
        );
      }
      this.addSpace(10);

      // Generate checklist questions table
      if (scenario.checklistData && scenario.checklistData.length > 0) {
        this.generateScenarioChecklistTable(scenario);
      }

      // Generate custom tasks table if any
      if (scenario.customTasks && scenario.customTasks.length > 0) {
        this.generateCustomTasksTable(scenario.customTasks);
      }

      // Add page break between scenarios (except for the last one)
      if (scenarioIndex < checklistData.checklistResponses.length - 1) {
        this.pdf.addPage();
        this.pageNumber++;
        this.currentY = this.margin + LAYOUT.headerHeight;
        this.addPageHeader();
      }
    });
  }

  /**
   * Generate checklist questions table for a scenario
   * @param {Object} scenario - Scenario data
   */
  generateScenarioChecklistTable(scenario) {
    this.addSectionHeading("Questions de v\u00e9rification", 2);
    this.addSpace(5);

    const headers = [
      "Mesure",
      "Question",
      "R\u00e9ponse",
      "Priorit\u00e9",
      "Preuve attendue",
    ];

    const rows = [];

    scenario.checklistData.forEach((measure) => {
      if (measure.questions && measure.questions.length > 0) {
        measure.questions.forEach((question) => {
          const questionId = question.id;
          const response = scenario.responses?.[questionId];

          // Format response with symbols
          const responseText =
            response === "fait" ? "F" : response === "pas_fait" ? "NF" : "-";

          // Format priority
          const priorityText =
            question.priority === "high"
              ? "\u00c9lev\u00e9e"
              : question.priority === "medium"
                ? "Moyenne"
                : question.priority === "low"
                  ? "Faible"
                  : "Non d\u00e9finie";

          rows.push([
            measure.measureTitle || "Mesure sans titre",
            question.text || "Question sans texte",
            responseText,
            priorityText,
            question.evidence || "Non sp\u00e9cifi\u00e9e",
          ]);
        });
      }
    });

    if (rows.length > 0) {
      this.addTable(headers, rows, {
        columnWidths: [35, 50, 20, 15, 40],
        alternateRows: true,
      });
    }

    this.addSpace(10);
  }

  /**
   * Generate custom tasks table
   * @param {Array} customTasks - Custom tasks array
   */
  generateCustomTasksTable(customTasks) {
    this.addSectionHeading("T\u00e2ches personnalis\u00e9es", 2);
    this.addSpace(5);

    const headers = [
      "T\u00e2che",
      "R\u00e9ponse",
      "Priorit\u00e9",
      "Preuve attendue",
    ];

    const rows = customTasks.map((task) => {
      const taskId = task.id;
      const response = task.response;

      // Format response with symbols
      const responseText =
        response === "fait" ? "F" : response === "pas_fait" ? "NF" : "-";

      // Format priority
      const priorityText =
        task.priority === "critical"
          ? "Critique"
          : task.priority === "high"
            ? "\u00c9lev\u00e9e"
            : task.priority === "medium"
              ? "Moyenne"
              : task.priority === "low"
                ? "Faible"
                : "Moyenne";

      return [
        task.text || "T\u00e2che sans description",
        responseText,
        priorityText,
        task.evidence || "Non sp\u00e9cifi\u00e9e",
      ];
    });

    if (rows.length > 0) {
      this.addTable(headers, rows, {
        columnWidths: [50, 20, 15, 45],
        alternateRows: true,
      });
    }

    this.addSpace(10);
  }

  /**
   * Generate risk sources and strategic scenarios section (Workshop 3)
   * @param {Object} reportData - Report data
   */
  generateRiskSourcesAndScenarios(reportData) {
    this.addSectionHeading(
      "3. SOURCES DE RISQUE ET SC\u00c9NARIOS STRAT\u00c9GIQUES",
      1,
    );
    this.addSpace(10);

    this.addParagraph(
      `Cette section pr\u00e9sente les r\u00e9sultats de l'atelier 3 d'EBIOS RM, consacr\u00e9 \u00e0 l'identification des sources de risque ` +
        `et \u00e0 l'\u00e9laboration des sc\u00e9narios strat\u00e9giques. L'objectif est d'identifier qui pourrait s'attaquer aux valeurs m\u00e9tier ` +
        `de l'organisation, avec quelles motivations et quels moyens, puis de mod\u00e9liser les chemins d'attaque possibles.`,
    );

    this.addSpace(10);

    // 3.1 Risk Sources
    this.generateRiskSourcesSection(reportData);

    // 3.2 Strategic Scenarios
    this.generateStrategicScenariosSection(reportData);

    // 3.3 Risk Assessment
    this.generateStrategicRiskAssessment(reportData);
  }

  /**
   * Generate operational scenarios section (Workshop 4)
   * @param {Object} reportData - Report data
   */
  generateOperationalScenarios(reportData) {
    this.addSectionHeading("4. SC\u00c9NARIOS OP\u00c9RATIONNELS", 1);
    this.addSpace(10);

    this.addParagraph(
      `L'atelier 4 d'EBIOS RM se concentre sur l'\u00e9laboration des sc\u00e9narios op\u00e9rationnels. Ces sc\u00e9narios d\u00e9taillent ` +
        `concr\u00e8tement comment les attaquants pourraient exploiter les vuln\u00e9rabilit\u00e9s techniques et organisationnelles ` +
        `pour r\u00e9aliser les sc\u00e9narios strat\u00e9giques identifi\u00e9s pr\u00e9c\u00e9demment. Cette analyse permet d'identifier ` +
        `les modes op\u00e9ratoires sp\u00e9cifiques et les vecteurs d'attaque les plus probables.`,
    );

    this.addSpace(10);

    // 4.1 Operational Scenarios Details
    this.generateOperationalScenariosDetails(reportData);

    // 4.2 Attack Paths Analysis
    this.generateAttackPathsAnalysis(reportData);

    // 4.3 Operational Risk Assessment
    this.generateOperationalRiskAssessment(reportData);
  }

  /**
   * Generate treatment plan section (Workshop 5)
   * @param {Object} reportData - Report data
   */
  generateTreatmentPlan(reportData) {
    this.addSectionHeading("5. PLAN DE TRAITEMENT DES RISQUES", 1);
    this.addSpace(10);

    this.addParagraph(
      `Le plan de traitement des risques constitue l'aboutissement de la d\u00e9marche EBIOS RM. Il pr\u00e9sente ` +
        `les mesures de s\u00e9curit\u00e9 recommand\u00e9es pour traiter les risques identifi\u00e9s, organis\u00e9es selon une approche ` +
        `structur\u00e9e prenant en compte les priorit\u00e9s, les co\u00fbts et les contraintes op\u00e9rationnelles de l'organisation.`,
    );

    this.addSpace(10);

    // 5.1 Risk Treatment Strategy
    this.generateRiskTreatmentStrategy(reportData);

    // 5.2 Security Measures
    this.generateSecurityMeasures(reportData);

    // 5.3 Implementation Roadmap
    this.generateImplementationRoadmap(reportData);

    // 5.4 Residual Risk Assessment
    this.generateResidualRiskAssessment(reportData);
  }

  /**
   * Generate risk sources subsection
   * @param {Object} reportData - Report data
   */
  generateRiskSourcesSection(reportData) {
    this.addSectionHeading("3.1. SOURCES DE RISQUE", 2);

    this.addParagraph(
      `Les sources de risque repr\u00e9sentent les entit\u00e9s susceptibles de porter atteinte aux valeurs m\u00e9tier ` +
        `de l'organisation. Elles sont caract\u00e9ris\u00e9es par leurs motivations, leurs ressources et leurs capacit\u00e9s. ` +
        `L'identification pr\u00e9cise de ces sources permet d'adapter les mesures de protection aux menaces r\u00e9elles.`,
    );

    this.addSpace(8);

    const riskSources = reportData.riskSources || [];
    if (riskSources.length > 0) {
      const sourcesHeaders = [
        "Source de Risque",
        "Type",
        "Motivation",
        "Ressources",
        "Expertise",
      ];
      const sourcesRows = riskSources.map((source) => [
        source.name || "Non d\u00e9finie",
        this.formatSourceType(source.type),
        source.motivation || "Non sp\u00e9cifi\u00e9e",
        this.formatResources(source.resources),
        this.formatExpertise(source.expertise),
      ]);

      this.addTable(sourcesHeaders, sourcesRows, {
        columnWidths: [35, 25, 30, 25, 25],
        alternateRows: true,
        fontSize: FONTS.small,
      });
    } else {
      this.addParagraph(
        "Aucune source de risque n'a \u00e9t\u00e9 identifi\u00e9e dans cette analyse.",
      );
    }

    this.addSpace(10);
  }

  /**
   * Generate strategic scenarios subsection
   * @param {Object} reportData - Report data
   */
  generateStrategicScenariosSection(reportData) {
    this.addSectionHeading("3.2. SC\u00c9NARIOS STRAT\u00c9GIQUES", 2);

    this.addParagraph(
      `Les sc\u00e9narios strat\u00e9giques d\u00e9crivent les chemins d'attaque de haut niveau que les sources de risque ` +
        `pourraient emprunter pour atteindre leurs objectifs. Ils \u00e9tablissent le lien entre les sources de risque ` +
        `et les \u00e9v\u00e9nements redout\u00e9s, en passant par les biens supports qui pourraient \u00eatre compromis.`,
    );

    this.addSpace(8);

    const strategicScenarios = reportData.strategicScenarios || [];
    if (strategicScenarios.length > 0) {
      strategicScenarios.forEach((scenario, index) => {
        this.addSectionHeading(
          `SC\u00c9NARIO ${index + 1}: ${scenario.name || "Sans nom"}`,
          3,
        );

        this.addParagraph(
          `Description: ${scenario.description || "Description non disponible"}`,
        );

        const scenarioHeaders = ["\u00c9l\u00e9ment", "D\u00e9tail"];
        const scenarioRows = [
          ["Source de risque", scenario.riskSource || "Non d\u00e9finie"],
          [
            "\u00c9v\u00e9nement redout\u00e9 vis\u00e9",
            scenario.dreadedEvent || "Non d\u00e9fini",
          ],
          [
            "Biens supports compromis",
            scenario.compromisedAssets?.join(", ") || "Non sp\u00e9cifi\u00e9s",
          ],
          ["Probabilit\u00e9", this.formatProbability(scenario.probability)],
          ["Impact", this.formatImpact(scenario.impact)],
          ["Niveau de risque", this.formatRiskLevel(scenario.riskLevel)],
        ];

        this.addTable(scenarioHeaders, scenarioRows, {
          columnWidths: [40, 100],
          alternateRows: true,
          fontSize: FONTS.small,
        });

        this.addSpace(8);
      });
    } else {
      this.addParagraph(
        "Aucun sc\u00e9nario strat\u00e9gique n'a \u00e9t\u00e9 d\u00e9fini dans cette analyse.",
      );
    }

    this.addSpace(10);
  }

  /**
   * Generate operational scenarios details
   * @param {Object} reportData - Report data
   */
  generateOperationalScenariosDetails(reportData) {
    this.addSectionHeading(
      "4.1. D\u00c9TAIL DES SC\u00c9NARIOS OP\u00c9RATIONNELS",
      2,
    );

    this.addParagraph(
      `Les sc\u00e9narios op\u00e9rationnels d\u00e9taillent les techniques, tactiques et proc\u00e9dures (TTP) que les attaquants ` +
        `pourraient utiliser pour concr\u00e9tiser les sc\u00e9narios strat\u00e9giques. Ils s'appuient sur des r\u00e9f\u00e9rentiels ` +
        `reconnus comme MITRE ATT&CK pour d\u00e9crire pr\u00e9cis\u00e9ment les modes op\u00e9ratoires.`,
    );

    this.addSpace(8);

    const operationalScenarios = reportData.operationalScenarios || [];
    if (operationalScenarios.length > 0) {
      operationalScenarios.forEach((scenario, index) => {
        this.addSectionHeading(
          `SC\u00c9NARIO OP\u00c9RATIONNEL ${index + 1}`,
          3,
        );

        this.addParagraph(`Nom: ${scenario.name || "Sans nom"}`);
        this.addParagraph(
          `Description: ${scenario.description || "Description non disponible"}`,
        );

        if (scenario.attackSteps && scenario.attackSteps.length > 0) {
          this.addSectionHeading("\u00c9tapes d'attaque:", 3);

          const stepsHeaders = [
            "\u00c9tape",
            "Technique MITRE ATT&CK",
            "Description",
            "Probabilit\u00e9",
          ];
          const stepsRows = scenario.attackSteps.map((step, stepIndex) => [
            `${stepIndex + 1}`,
            step.mitreId || "Non r\u00e9f\u00e9renc\u00e9e",
            step.description || "Non d\u00e9crite",
            this.formatProbability(step.probability),
          ]);

          this.addTable(stepsHeaders, stepsRows, {
            columnWidths: [15, 35, 70, 20],
            alternateRows: true,
            fontSize: FONTS.small,
          });
        }

        this.addSpace(8);
      });
    } else {
      this.addParagraph(
        "Aucun sc\u00e9nario op\u00e9rationnel n'a \u00e9t\u00e9 d\u00e9fini dans cette analyse.",
      );
    }

    this.addSpace(10);
  }

  /**
   * Generate security measures subsection
   * @param {Object} reportData - Report data
   */
  generateSecurityMeasures(reportData) {
    this.addSectionHeading(
      "5.2. MESURES DE S\u00c9CURIT\u00c9 RECOMMAND\u00c9ES",
      2,
    );

    this.addParagraph(
      `Cette section pr\u00e9sente l'ensemble des mesures de s\u00e9curit\u00e9 recommand\u00e9es pour traiter les risques identifi\u00e9s. ` +
        `Les mesures sont organis\u00e9es par cat\u00e9gorie et prioris\u00e9es selon leur efficacit\u00e9, leur co\u00fbt et leur faisabilit\u00e9. ` +
        `Chaque mesure est associ\u00e9e aux risques qu'elle permet de traiter.`,
    );

    this.addSpace(8);

    const treatmentPlans = reportData.treatmentPlans || [];
    if (treatmentPlans.length > 0) {
      // Group measures by category
      const measuresByCategory = this.groupMeasuresByCategory(treatmentPlans);

      Object.keys(measuresByCategory).forEach((category) => {
        this.addSectionHeading(`CAT\u00c9GORIE: ${category.toUpperCase()}`, 3);

        const categoryMeasures = measuresByCategory[category];
        const measuresHeaders = [
          "Mesure",
          "Description",
          "Priorit\u00e9",
          "Co\u00fbt Estim\u00e9",
          "D\u00e9lai",
        ];
        const measuresRows = categoryMeasures.map((measure) => [
          measure.name || "Non d\u00e9finie",
          measure.description || "Description non disponible",
          this.formatPriority(measure.priority),
          this.formatCost(measure.estimatedCost),
          measure.timeline || "Non d\u00e9fini",
        ]);

        this.addTable(measuresHeaders, measuresRows, {
          columnWidths: [35, 50, 20, 20, 15],
          alternateRows: true,
          fontSize: FONTS.small,
        });

        this.addSpace(8);
      });
    } else {
      this.addParagraph(
        "Aucune mesure de s\u00e9curit\u00e9 n'a \u00e9t\u00e9 recommand\u00e9e dans cette analyse.",
      );
    }

    this.addSpace(10);
  }

  /**
   * Helper methods for formatting various data types
   */
  formatSourceType(type) {
    const types = {
      internal: "Interne",
      external: "Externe",
      partner: "Partenaire",
      state: "\u00c9tatique",
      criminal: "Criminel",
    };
    return types[type] || "Non d\u00e9fini";
  }

  formatResources(resources) {
    const levels = {
      low: "Limit\u00e9es",
      medium: "Moyennes",
      high: "Importantes",
      unlimited: "Illimit\u00e9es",
    };
    return levels[resources] || "Non d\u00e9finies";
  }

  formatExpertise(expertise) {
    const levels = {
      low: "Faible",
      medium: "Moyenne",
      high: "\u00c9lev\u00e9e",
      expert: "Expert",
    };
    return levels[expertise] || "Non d\u00e9finie";
  }

  formatProbability(probability) {
    const levels = {
      very_low: "Tr\u00e8s faible",
      low: "Faible",
      medium: "Moyenne",
      high: "\u00c9lev\u00e9e",
      very_high: "Tr\u00e8s \u00e9lev\u00e9e",
    };
    return levels[probability] || "Non d\u00e9finie";
  }

  formatImpact(impact) {
    const levels = {
      low: "Faible",
      medium: "Moyen",
      high: "\u00c9lev\u00e9",
      critical: "Critique",
    };
    return levels[impact] || "Non d\u00e9fini";
  }

  formatRiskLevel(riskLevel) {
    const levels = {
      low: "Faible",
      medium: "Moyen",
      high: "\u00c9lev\u00e9",
      critical: "Critique",
    };
    return levels[riskLevel] || "Non d\u00e9fini";
  }

  formatPriority(priority) {
    const priorities = {
      critical: "Critique",
      high: "\u00c9lev\u00e9e",
      medium: "Moyenne",
      low: "Faible",
    };
    return priorities[priority] || "Non d\u00e9finie";
  }

  formatCost(cost) {
    if (typeof cost === "number") {
      return `${cost.toLocaleString("fr-FR")} €`;
    }
    const costs = {
      low: "< 10k€",
      medium: "10k€ - 50k€",
      high: "50k€ - 200k€",
      very_high: "> 200k€",
    };
    return costs[cost] || "Non estim\u00e9";
  }

  /**
   * Group measures by category
   * @param {Array} treatmentPlans - Treatment plans array
   * @returns {Object} Measures grouped by category
   */
  groupMeasuresByCategory(treatmentPlans) {
    const grouped = {};

    treatmentPlans.forEach((plan) => {
      if (plan.measures && Array.isArray(plan.measures)) {
        plan.measures.forEach((measure) => {
          const category = measure.category || "Autres";
          if (!grouped[category]) {
            grouped[category] = [];
          }
          grouped[category].push(measure);
        });
      }
    });

    return grouped;
  }

  /**
   * Generate implementation roadmap
   * @param {Object} reportData - Report data
   */
  generateImplementationRoadmap(reportData) {
    this.addSectionHeading("5.3. FEUILLE DE ROUTE D'IMPL\u00c9MENTATION", 2);

    this.addParagraph(
      `La feuille de route pr\u00e9sente la planification recommand\u00e9e pour la mise en \u0153uvre des mesures de s\u00e9curit\u00e9. ` +
        `Elle organise les actions selon des phases logiques, en tenant compte des d\u00e9pendances, des priorit\u00e9s ` +
        `et des contraintes organisationnelles.`,
    );

    this.addSpace(8);

    // Implementation phases
    const phases = [
      {
        name: "Phase 1 - Actions Imm\u00e9diates (0-3 mois)",
        description: "Mesures critiques \u00e0 d\u00e9ployer en urgence",
        color: COLORS.danger,
      },
      {
        name: "Phase 2 - Court Terme (3-6 mois)",
        description: "Renforcement des contr\u00f4les prioritaires",
        color: COLORS.warning,
      },
      {
        name: "Phase 3 - Moyen Terme (6-12 mois)",
        description: "Am\u00e9lioration continue et optimisation",
        color: COLORS.info,
      },
      {
        name: "Phase 4 - Long Terme (12+ mois)",
        description: "Transformation et innovation s\u00e9curitaire",
        color: COLORS.success,
      },
    ];

    phases.forEach((phase) => {
      this.addSectionHeading(phase.name, 3);
      this.addParagraph(phase.description);

      // Add placeholder for phase-specific measures
      this.addParagraph(
        "• Mesures sp\u00e9cifiques \u00e0 d\u00e9finir selon les r\u00e9sultats de l'analyse",
      );
      this.addParagraph("• Allocation des ressources et responsabilit\u00e9s");
      this.addParagraph("• Indicateurs de suivi et jalons de validation");

      this.addSpace(8);
    });

    this.addSpace(10);
  }

  /**
   * Clean text to remove problematic characters
   * @param {string} text - Text to clean
   * @param {number} maxLength - Maximum length (default 500)
   * @returns {string} Cleaned text
   */
  cleanText(text, maxLength = 500) {
    if (!text || typeof text !== "string") return "";

    let cleaned = text
      .replace(/[^\x20-\x7E\u00C0-\u017F\u00C7\u00E7\u00E0-\u00FF]/g, "") // Keep French characters
      .replace(/\s+/g, " ") // Normalize whitespace
      .replace(/[\r\n]+/g, " ") // Replace line breaks with spaces
      .trim();

    // Limit length and add ellipsis if needed
    if (cleaned.length > maxLength) {
      cleaned = cleaned.substring(0, maxLength - 3) + "...";
    }

    return cleaned;
  }
}

export default EnhancedPDFGenerator;
